<template>
  <div
  class="company-admin-page"
  :class="isDarkTheme ? 'theme-dark' : 'theme-light'"
  >

    <!-- =====================================================
         HEADER
    ====================================================== -->

    <DefaultHeader
      :title="company?.name ? `${company.name} · Admin` : 'Компания'"
      class="fixed-header"
      :offset="680"
    />

    <!-- =====================================================
         LOADING
    ====================================================== -->

    <div v-if="isLoading" class="page-shell loading-shell">
      <div class="hero-skeleton skeleton"></div>

      <div class="skeleton-grid">
        <div class="card-skeleton skeleton"></div>
        <div class="card-skeleton skeleton"></div>
      </div>

      <div class="wide-skeleton skeleton"></div>
    </div>

    <!-- =====================================================
         PAGE
    ====================================================== -->

    <main
      v-else-if="company"
      class="page-shell"
    >

      <!-- ===================================================
           PAGE INTRO
      ==================================================== -->

      <section class="page-intro">
        <div>
          <div class="eyebrow">
            HR3 ADMIN
          </div>

          <h1>
            Управление компанией
          </h1>

          <p>
            Редактирование профиля, визуального контента,
            адреса, социальных сетей и материалов для вакансий.
          </p>
        </div>

        <div class="admin-status">
          <span class="admin-status-dot"></span>
          Режим администратора
        </div>
      </section>


      <!-- ===================================================
           HERO
      ==================================================== -->

      <section class="company-hero">

        <div class="cover-area">

          <img
            v-if="company.urlOfBackground"
            :src="company.urlOfBackground"
            class="cover-image"
            :class="{ visible: isBackgroundLoaded }"
            alt=""
            @load="isBackgroundLoaded = true"
          />

          <div
            v-else
            class="cover-placeholder"
          >
            <div class="cover-placeholder-shape shape-one"></div>
            <div class="cover-placeholder-shape shape-two"></div>
          </div>

          <div class="cover-overlay"></div>

          <input
            ref="bannerInputRef"
            type="file"
            accept="image/*"
            hidden
            @change="onBannerPicked"
          />

          <button
            type="button"
            class="media-edit-button banner-edit"
            :disabled="bannerUploading"
            @click="onBannerClick"
          >
            <span
              v-if="bannerUploading"
              class="small-spinner"
            ></span>

            <svg
              v-else
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M4 16.5V20H7.5L18.2 9.3L14.7 5.8L4 16.5Z"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linejoin="round"
              />

              <path
                d="M13.8 6.7L17.3 10.2"
                stroke="currentColor"
                stroke-width="1.7"
              />
            </svg>

            Изменить обложку
          </button>

        </div>


        <div class="hero-profile">

          <!-- LOGO -->

          <div
            class="logo-shell"
            @click="onLogoTap"
          >
            <img
              v-if="company.urlOfLogo"
              :src="company.urlOfLogo"
              class="company-logo"
              alt=""
            />

            <div
              v-else
              class="logo-placeholder"
            >
              {{ companyInitials }}
            </div>

            <div class="logo-edit-overlay">
              <span
                v-if="logoUploading"
                class="small-spinner"
              ></span>

              <svg
                v-else
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M4 16.5V20H7.5L18.2 9.3L14.7 5.8L4 16.5Z"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          </div>

          <input
            ref="logoInput"
            type="file"
            accept="image/*"
            hidden
            @change="onLogoPicked"
          />


          <!-- COMPANY TITLE -->

          <div class="hero-company-info">

            <div class="hero-title-row">
              <h2>
                {{ company.name || "Компания без названия" }}
              </h2>

              <span
                class="vacancy-photo-badge"
                :class="{
                  ready: company.isImagesUploaded === true,
                  missing: company.isImagesUploaded !== true
                }"
              >
                <span class="badge-dot"></span>

                {{
                  company.isImagesUploaded === true
                    ? "Фото для вакансий загружены"
                    : "Нужны фото для вакансий"
                }}
              </span>
            </div>

            <div class="hero-meta">

              <button
                type="button"
                class="location-pill"
                @click="openCompanyRegionDrawer"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M12 21C12 21 18 15.5 18 10A6 6 0 1 0 6 10C6 15.5 12 21 12 21Z"
                    stroke="currentColor"
                    stroke-width="1.6"
                  />

                  <circle
                    cx="12"
                    cy="10"
                    r="2"
                    stroke="currentColor"
                    stroke-width="1.6"
                  />
                </svg>

                {{ getRegionNameById(company.regionId) }}
              </button>

              <span
                v-if="company.specifics"
                class="specialization-pill"
              >
                {{ company.specifics }}
              </span>

            </div>

          </div>

        </div>

      </section>


      <!-- ===================================================
           MAIN GRID
      ==================================================== -->

      <div class="dashboard-grid">

        <!-- =================================================
             BASIC INFO
        ================================================== -->

        <section class="dashboard-card main-info-card">

          <div class="card-heading">
            <div>
              <span class="card-label">
                Профиль
              </span>

              <h3>
                Основная информация
              </h3>

              <p>
                Основные данные, которые используются
                в профиле компании.
              </p>
            </div>

            <div class="section-icon blue">
              <svg
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M4 20V7L12 3L20 7V20"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linejoin="round"
                />

                <path
                  d="M8 20V11H16V20"
                  stroke="currentColor"
                  stroke-width="1.6"
                />
              </svg>
            </div>
          </div>


          <div class="form-grid">

            <label class="modern-field full">
              <span>
                Название компании
              </span>

              <input
                v-model="company.name"
                type="text"
                placeholder="Название компании"
              />
            </label>


            <div class="modern-field full alternative-names-field">
              <span>
                Альтернативные названия
              </span>

              <div
                v-if="company.alternativeNames?.length"
                class="alternative-names-list"
              >
                <div
                  v-for="(name, index) in company.alternativeNames"
                  :key="`${name}-${index}`"
                  class="alternative-name-chip"
                >
                  <span>
                    {{ name }}
                  </span>

                  <button
                    type="button"
                    class="alternative-name-remove"
                    @click="removeAlternativeName(index)"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div class="alternative-name-input-row">
                <input
                  v-model="newAlternativeName"
                  type="text"
                  placeholder="Например, Lifehacker"
                  @keyup.enter="addAlternativeName"
                />

                <button
                  type="button"
                  class="alternative-name-add-button"
                  :disabled="!newAlternativeName.trim()"
                  @click="addAlternativeName"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M12 5V19M5 12H19"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                    />
                  </svg>

                  Добавить
                </button>
              </div>

              <small class="field-hint">
                Используются для поиска и сопоставления компании.
              </small>
            </div>


            <label class="modern-field">
              <span>
                Специализация
              </span>

              <input
                v-model="company.specifics"
                type="text"
                placeholder="Например, IT / Design"
              />
            </label>


            <label class="modern-field">
              <span>
                Email
              </span>

              <input
                v-model="company.email"
                type="email"
                placeholder="hello@company.com"
              />
            </label>


            <label class="modern-field full">
              <span>
                Website
              </span>

              <input
                v-model="company.website"
                type="text"
                placeholder="https://company.com"
              />
            </label>


            <label class="modern-field full">
              <span>
                О компании
              </span>

              <textarea
                v-model="company.aboutCompany"
                rows="7"
                placeholder="Расскажите о компании..."
              ></textarea>
            </label>

          </div>


          <div class="card-actions">
            <span
              v-if="hasCompanyChanges"
              class="unsaved-status"
            >
              Есть несохранённые изменения
            </span>

            <span
              v-else
              class="saved-status"
            >
              Всё сохранено
            </span>

            <button
              type="button"
              class="primary-button"
              :disabled="!hasCompanyChanges || companySaving"
              @click="saveCompany"
            >
              <span
                v-if="companySaving"
                class="button-spinner"
              ></span>

              {{
                companySaving
                  ? "Сохраняем..."
                  : "Сохранить изменения"
              }}
            </button>
          </div>

        </section>


        <!-- =================================================
             OFFICE
        ================================================== -->

        <section class="dashboard-card office-card">

          <div class="card-heading">
            <div>
              <span class="card-label">
                Локация
              </span>

              <h3>
                Офис
              </h3>

              <p>
                Адрес и точка компании на карте.
              </p>
            </div>

            <div class="section-icon purple">
              <svg
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M12 21C12 21 18 15.5 18 10A6 6 0 1 0 6 10C6 15.5 12 21 12 21Z"
                  stroke="currentColor"
                  stroke-width="1.6"
                />

                <circle
                  cx="12"
                  cy="10"
                  r="2"
                  stroke="currentColor"
                  stroke-width="1.6"
                />
              </svg>
            </div>
          </div>


          <div class="office-address">
            <div class="office-address-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M5 20V9L12 5L19 9V20"
                  stroke="currentColor"
                  stroke-width="1.6"
                />
              </svg>
            </div>

            <div>
              <span>
                Текущий адрес
              </span>

              <strong>
                {{
                  company.companyLocation?.address ||
                  "Адрес не указан"
                }}
              </strong>
            </div>
          </div>


          <div class="map-container">
            <ChooseGoogleMap
              :lat="company.companyLocation?.latitude"
              :lng="company.companyLocation?.longitude"
              :isEnabled="true"
              :companyId="company.id"
              @locationSelected="saveLocation"
            />
          </div>

        </section>


        <!-- =================================================
             SOCIAL
        ================================================== -->

        <section class="dashboard-card social-card">

          <div class="card-heading">
            <div>
              <span class="card-label">
                Контакты
              </span>

              <h3>
                Социальные сети
              </h3>

              <p>
                Ссылки на официальные страницы компании.
              </p>
            </div>

            <div class="section-icon green">
              <svg
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  cx="6"
                  cy="12"
                  r="2.5"
                  stroke="currentColor"
                  stroke-width="1.6"
                />

                <circle
                  cx="18"
                  cy="6"
                  r="2.5"
                  stroke="currentColor"
                  stroke-width="1.6"
                />

                <circle
                  cx="18"
                  cy="18"
                  r="2.5"
                  stroke="currentColor"
                  stroke-width="1.6"
                />

                <path
                  d="M8.2 10.8L15.7 7.2M8.2 13.2L15.7 16.8"
                  stroke="currentColor"
                  stroke-width="1.6"
                />
              </svg>
            </div>
          </div>


          <div class="social-grid">

            <label
              v-for="item in SOCIAL_ICONS_EDIT"
              :key="item.key"
              class="social-input"
            >
              <div
                class="social-icon-box"
                :class="{
                  active:
                    company.social?.[item.key]?.length > 0
                }"
              >
                <img
                  :src="item.icon"
                  alt=""
                />
              </div>

              <div class="social-input-content">
                <span>
                  {{ item.label }}
                </span>

                <input
                  v-model="company.social[item.key]"
                  type="text"
                  :placeholder="item.placeholder"
                />
              </div>
            </label>

          </div>


          <div class="card-actions">

            <span
              v-if="hasSocialChanges"
              class="unsaved-status"
            >
              Есть изменения
            </span>

            <span
              v-else
              class="saved-status"
            >
              Всё сохранено
            </span>

            <button
              type="button"
              class="secondary-button"
              :disabled="!hasSocialChanges || socialSaving"
              @click="saveSocial"
            >
              <span
                v-if="socialSaving"
                class="button-spinner dark"
              ></span>

              {{
                socialSaving
                  ? "Сохраняем..."
                  : "Сохранить соцсети"
              }}
            </button>

          </div>

        </section>

      </div>


      <!-- ===================================================
           COMPANY EXTRA PHOTOS
      ==================================================== -->

      <section class="dashboard-card wide-card company-gallery-card">

        <div class="card-heading gallery-heading">
          <div>
            <span class="card-label">
              Профиль компании
            </span>

            <h3>
              Дополнительные фото компании
            </h3>

            <p>
              Фото офиса, команды или мероприятий.
              Это пользовательская галерея профиля компании
              и она не относится к изображениям вакансий.
            </p>
          </div>

          <div class="gallery-type-badge">
            Пользовательские фото
          </div>
        </div>


        <div class="company-gallery">

          <button
            v-for="i in 3"
            :key="i"
            type="button"
            class="company-photo-slot"
            :class="{
              filled: extraImages[i - 1]
            }"
            @click="selectExtraImage(i - 1)"
          >

            <div
              class="company-photo-content"
              :class="{
                deleting:
                  extraDeleting[i - 1]
              }"
            >

              <img
                v-if="extraImages[i - 1]"
                :src="extraImages[i - 1]"
                alt=""
              />

              <div
                v-else
                class="photo-placeholder"
              >
                <div class="photo-plus">
                  +
                </div>

                <span>
                  Добавить фото
                </span>
              </div>


              <button
                v-if="extraImages[i - 1]"
                type="button"
                class="photo-delete-button"
                @click.stop="removeExtraImage(i - 1)"
              >
                <span
                  v-if="extraDeleting[i - 1]"
                  class="tiny-spinner"
                ></span>

                <svg
                  v-else
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M6 6L18 18M18 6L6 18"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                  />
                </svg>
              </button>

            </div>


            <div
              v-if="extraUploading[i - 1]"
              class="photo-upload-overlay"
            >
              <span class="button-spinner"></span>
            </div>

          </button>

        </div>


        <input
          ref="extraInput"
          type="file"
          accept="image/*"
          hidden
          @change="onExtraPicked"
        />

      </section>


      <!-- ===================================================
           VACANCY IMAGES ADMIN
      ==================================================== -->

      <section class="vacancy-images-card">

        <div class="vacancy-images-glow"></div>

        <div class="vacancy-admin-header">

          <div class="vacancy-admin-title">

            <div class="vacancy-admin-eyebrow">
              <span>ADMIN</span>
              Контент для вакансий
            </div>

            <h3>
              Загрузка доп фотографий для вакансий
            </h3>

            <p>
              Загрузите 3–4 качественные фотографии,
              которые HR3 сможет использовать как визуальный
              материал для вакансий этой компании.
            </p>

          </div>


          <div
            class="vacancy-images-status"
            :class="{
              ready:
                company.isImagesUploaded === true,

              missing:
                company.isImagesUploaded !== true
            }"
          >
            <span class="status-ring">
              <span class="status-dot"></span>
            </span>

            <div>
              <span>
                Статус
              </span>

              <strong>
                {{
                  company.isImagesUploaded === true
                    ? "Изображения загружены"
                    : "Изображений пока нет"
                }}
              </strong>
            </div>
          </div>

        </div>


        <!-- DROP ZONE -->

        <div
          class="vacancy-dropzone"
          :class="{
            dragging: vacancyImagesDragging,
            disabled: vacancyImagesUploading
          }"
          @click="openVacancyImagesPicker"
          @dragenter.prevent="vacancyImagesDragging = true"
          @dragover.prevent="vacancyImagesDragging = true"
          @dragleave.prevent="vacancyImagesDragging = false"
          @drop.prevent="onVacancyImagesDrop"
        >

          <input
            ref="vacancyImagesInput"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            hidden
            @change="onVacancyImagesPicked"
          />


          <div class="drop-icon">

            <svg
              viewBox="0 0 32 32"
              fill="none"
            >
              <rect
                x="4"
                y="6"
                width="24"
                height="20"
                rx="5"
                stroke="currentColor"
                stroke-width="1.7"
              />

              <circle
                cx="11"
                cy="13"
                r="2.2"
                stroke="currentColor"
                stroke-width="1.7"
              />

              <path
                d="M7 23L13.5 17L17 20L20 17.5L25 23"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
                stroke-linejoin="round"
              />

              <path
                d="M24 3V11M20 7H28"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />
            </svg>

          </div>


          <div class="drop-title">
            Перетащите фотографии сюда
          </div>

          <div class="drop-subtitle">
            или нажмите, чтобы выбрать их на компьютере
          </div>


          <div class="drop-requirements">
            <span>JPG</span>
            <span>PNG</span>
            <span>WEBP</span>

            <div class="requirement-divider"></div>

            <span>
              до 15 MB
            </span>

            <div class="requirement-divider"></div>

            <span>
              максимум 4
            </span>
          </div>

        </div>


        <!-- PREVIEWS -->

        <TransitionGroup
          name="vacancy-preview"
          tag="div"
          class="vacancy-preview-grid"
        >

          <div
            v-for="(preview, index) in vacancyImagePreviews"
            :key="preview.id"
            class="vacancy-preview-card"
          >

            <div class="vacancy-preview-image">
              <img
                :src="preview.url"
                alt=""
              />

              <div class="preview-index">
                {{ index + 1 }}
              </div>

              <button
                type="button"
                class="preview-remove"
                :disabled="vacancyImagesUploading"
                @click.stop="removeVacancyImage(index)"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M6 6L18 18M18 6L6 18"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                  />
                </svg>
              </button>
            </div>


            <div class="preview-file-info">

              <span class="preview-file-name">
                {{ preview.file.name }}
              </span>

              <span class="preview-file-size">
                {{ formatFileSize(preview.file.size) }}
              </span>

            </div>

          </div>

        </TransitionGroup>


        <!-- BOTTOM -->

        <div class="vacancy-upload-footer">

          <div class="vacancy-selection-info">

            <div class="selection-count">
              <strong>
                {{ vacancyImageFiles.length }}
              </strong>

              <span>
                / {{ MAX_VACANCY_IMAGES }}
              </span>
            </div>

            <div>
              <strong>
                Выбрано фотографий
              </strong>

              <span>
                {{
                  vacancyImageFiles.length
                    ? "Можно загружать"
                    : "Добавьте несколько изображений"
                }}
              </span>
            </div>

          </div>


          <button
            type="button"
            class="vacancy-upload-button"
            :disabled="
              vacancyImageFiles.length === 0 ||
              vacancyImagesUploading
            "
            @click="uploadVacancyImages"
          >
            <span
              v-if="vacancyImagesUploading"
              class="button-spinner"
            ></span>

            <svg
              v-else
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12 16V5M8 9L12 5L16 9"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />

              <path
                d="M5 15V18C5 19.1 5.9 20 7 20H17C18.1 20 19 19.1 19 18V15"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />
            </svg>

            {{
              vacancyImagesUploading
                ? "Загружаем..."
                : vacancyImageFiles.length
                  ? `Загрузить ${vacancyImageFiles.length} фото`
                  : "Выберите фотографии"
            }}
          </button>

        </div>

      </section>


      <!-- ===================================================
           PERKS
      ==================================================== -->

      <section class="dashboard-card wide-card perks-card">

        <div class="card-heading">
          <div>
            <span class="card-label">
              Employer brand
            </span>

            <h3>
              Преимущества компании
            </h3>

            <p>
              Выберите преимущества,
              которые будут отображаться в профиле.
            </p>
          </div>

          <div class="section-icon orange">
            <svg
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12 21S4 16.5 4 10.5A4.5 4.5 0 0 1 12 7.7A4.5 4.5 0 0 1 20 10.5C20 16.5 12 21 12 21Z"
                stroke="currentColor"
                stroke-width="1.6"
              />
            </svg>
          </div>
        </div>


        <div class="perks-grid">

          <button
            v-for="perk in ALL_BENEFITS"
            :key="perk.id"
            type="button"
            class="perk-chip"
            :class="{
              selected:
                selectedPerks.includes(perk.id)
            }"
            @click="togglePerk(perk.id)"
          >
            <span
              class="perk-check"
              :class="{
                visible:
                  selectedPerks.includes(perk.id)
              }"
            >
              ✓
            </span>

            {{ perk.ruName }}
          </button>

        </div>


        <div class="card-actions">

          <span
            v-if="hasPerksChanges"
            class="unsaved-status"
          >
            Список изменён
          </span>

          <span
            v-else
            class="saved-status"
          >
            Всё сохранено
          </span>

          <button
            type="button"
            class="secondary-button"
            :disabled="!hasPerksChanges || perksSaving"
            @click="savePerks"
          >
            <span
              v-if="perksSaving"
              class="button-spinner dark"
            ></span>

            {{
              perksSaving
                ? "Сохраняем..."
                : "Сохранить преимущества"
            }}
          </button>

        </div>

      </section>

    </main>


    <!-- =====================================================
         REGION DRAWER
    ====================================================== -->

    <CompanyRegionDrawer
      v-if="company && showCompanyRegionDrawer"
      :visible="showCompanyRegionDrawer"
      :modelValue="company.regionId"
      :companyId="company.id"
      @update:modelValue="onCompanyRegionSelected"
      @close="showCompanyRegionDrawer = false"
    />


    <!-- =====================================================
         SNACKBAR
    ====================================================== -->

    <Snackbar
      :message="snackbarMessage"
      :show="showSnackbar"
      :type="snackbarType"
    />


    <Footer />
  </div>
</template>


<script setup>

import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref
} from "vue";

import { useRoute } from "vue-router";

import DefaultHeader from "../components/DefaultHeader.vue";
import Footer from "../components/MiniFooter.vue";
import Snackbar from "../components/Snackbar.vue";
import ChooseGoogleMap from "../components/ChooseAdminGoogleMap.vue";
import CompanyRegionDrawer from "../components/CompanyAdminRegionDrawer.vue";

import { SettingsService } from "../api/SettingsService.js";
import CompanyService from "../api/CompanyService.js";
import AdminService from "../api/AdminService.js";
import RegionService from "../utils/RegionService.js";

import facebookIcon from "../assets/facebook-profile.png";
import instagramIcon from "../assets/instagram-profile.png";
import linkedinIcon from "../assets/linkedin-profile.png";
import twitterIcon from "../assets/x-profile.png";
import telegramIcon from "../assets/telegram-profile.png";
import youtubeIcon from "../assets/youtube-profile.png";


/* =========================================================
   ROUTE
========================================================= */

const route = useRoute();

const isDarkTheme = ref(false);

let themeObserver = null;

function updateLocalTheme() {
  isDarkTheme.value =
    document.documentElement.getAttribute("data-theme") === "dark";
}


/* =========================================================
   MAIN STATE
========================================================= */

const company = ref(null);
const originalCompany = ref({});

const isLoading = ref(true);
const isBackgroundLoaded = ref(false);

const companySaving = ref(false);
const socialSaving = ref(false);
const perksSaving = ref(false);

const bannerUploading = ref(false);
const logoUploading = ref(false);

const showCompanyRegionDrawer = ref(false);


/* =========================================================
   SNACKBAR
========================================================= */

const showSnackbar = ref(false);
const snackbarMessage = ref("");
const snackbarType = ref("success");

let snackbarTimer = null;


/* =========================================================
   INPUTS
========================================================= */

const bannerInputRef = ref(null);
const logoInput = ref(null);
const extraInput = ref(null);
const vacancyImagesInput = ref(null);
const newAlternativeName = ref("");

/* =========================================================
   SOCIAL
========================================================= */

const SOCIAL_ICONS_EDIT = [
  {
    key: "facebook",
    label: "Facebook",
    icon: facebookIcon,
    placeholder: "facebook.com/..."
  },
  {
    key: "instagram",
    label: "Instagram",
    icon: instagramIcon,
    placeholder: "instagram.com/..."
  },
  {
    key: "linkedin",
    label: "LinkedIn",
    icon: linkedinIcon,
    placeholder: "linkedin.com/company/..."
  },
  {
    key: "twitter",
    label: "X",
    icon: twitterIcon,
    placeholder: "x.com/..."
  },
  {
    key: "telegram",
    label: "Telegram",
    icon: telegramIcon,
    placeholder: "t.me/..."
  },
  {
    key: "youtube",
    label: "YouTube",
    icon: youtubeIcon,
    placeholder: "youtube.com/..."
  }
];


/* =========================================================
   BENEFITS
========================================================= */

const ALL_BENEFITS = [
  { id: "01", ruName: "Оплачиваемые отгулы" },
  { id: "02", ruName: "Гибкий график работы" },
  { id: "03", ruName: "Абонемент в спортзал" },
  { id: "04", ruName: "Укороченные дни перед праздниками" },
  { id: "05", ruName: "Оплачиваемый декрет" },
  { id: "06", ruName: "Холодильник с едой" },
  { id: "07", ruName: "Различные мероприятия" },
  { id: "08", ruName: "Волонтерские дни" },
  { id: "09", ruName: "Pet-friendly офис" },
  { id: "10", ruName: "Оплачиваемые обучения" },
  { id: "11", ruName: "Удаленка" },
  { id: "12", ruName: "Оплачиваемый проезд" },
  { id: "13", ruName: "Помощь по уходу за детьми" },
  { id: "14", ruName: "Программа переквалификации" },
  { id: "15", ruName: "Игровая комната" },
  { id: "16", ruName: "Медицинская страховка" },
  { id: "17", ruName: "Внутренние сервисы" }
];

const selectedPerks = ref([]);


/* =========================================================
   COMPANY EXTRA IMAGES
   Старый пользовательский раздел
========================================================= */

const extraImages = ref([]);
const extraUploading = ref([false, false, false]);
const extraDeleting = ref([false, false, false]);

let extraIndex = null;


/* =========================================================
   VACANCY IMAGES
   Новый ADMIN раздел
========================================================= */

const MAX_VACANCY_IMAGES = 4;
const MAX_VACANCY_IMAGE_SIZE = 15 * 1024 * 1024;

const vacancyImageFiles = ref([]);
const vacancyImagePreviews = ref([]);

const vacancyImagesDragging = ref(false);
const vacancyImagesUploading = ref(false);


/* =========================================================
   COMPUTED
========================================================= */

const companyInitials = computed(() => {
  const name = company.value?.name?.trim();

  if (!name) {
    return "HR";
  }

  const words = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);

  return words
    .map(word => word[0])
    .join("")
    .toUpperCase();
});


const hasCompanyChanges = computed(() => {
  if (!company.value || !originalCompany.value) {
    return false;
  }

  const fields = [
    "name",
    "specifics",
    "email",
    "website",
    "aboutCompany"
  ];

  const basicChanged =
    fields.some(field => {
      return (
        (company.value[field] || "") !==
        (originalCompany.value[field] || "")
      );
    });

  const alternativeNamesChanged =
    JSON.stringify(
      company.value.alternativeNames || []
    ) !==
    JSON.stringify(
      originalCompany.value.alternativeNames || []
    );

  return (
    basicChanged ||
    alternativeNamesChanged
  );
});


const hasSocialChanges = computed(() => {
  if (!company.value || !originalCompany.value) {
    return false;
  }

  const keys = [
    "facebook",
    "instagram",
    "linkedin",
    "twitter",
    "telegram",
    "youtube"
  ];

  return keys.some(key => {
    return (
      (company.value.social?.[key] || "") !==
      (originalCompany.value.social?.[key] || "")
    );
  });
});


const hasPerksChanges = computed(() => {
  const original = (
    originalCompany.value?.perksAndBenefits || []
  ).map(item => item.id || item);

  return (
    JSON.stringify(original) !==
    JSON.stringify(selectedPerks.value)
  );
});


/* =========================================================
   HELPERS
========================================================= */

function showMessage(message, type = "success") {
  snackbarMessage.value = message;
  snackbarType.value = type;
  showSnackbar.value = true;

  if (snackbarTimer) {
    clearTimeout(snackbarTimer);
  }

  snackbarTimer = window.setTimeout(() => {
    showSnackbar.value = false;
  }, 3200);
}


function getRegionNameById(regionId) {
  return (
    RegionService.handleRegion(regionId) ||
    "Регион не указан"
  );
}


function formatFileSize(bytes) {
  if (!bytes) {
    return "0 KB";
  }

  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}


function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;

    reader.readAsDataURL(file);
  });
}


/* =========================================================
   ACCESS
========================================================= */

async function checkAdminAccess() {
  try {
    const settings = await SettingsService.fetchSettings();

    if (!settings || settings.isAdmin !== true) {
      window.location.replace("https://hr3.space");
      return false;
    }

    return true;
  } catch (error) {
    console.error(
      "❌ Ошибка проверки администратора:",
      error
    );

    window.location.replace("https://hr3.space");

    return false;
  }
}


/* =========================================================
   LOAD
========================================================= */

onMounted(async () => {

  updateLocalTheme();

themeObserver = new MutationObserver(() => {
  updateLocalTheme();
});

themeObserver.observe(
  document.documentElement,
  {
    attributes: true,
    attributeFilter: ["data-theme"]
  }
);

  const isAdmin = await checkAdminAccess();

  if (!isAdmin) {
    return;
  }

  window.scrollTo({
    top: 0,
    behavior: "instant"
  });

  const companyId = route.params.companyId;

  try {
    const data =
      await CompanyService.getCompanyWithEditingOptions(
        companyId,
        true
      );

    company.value = data;

    company.value.social = {
      facebook: data.social?.facebook_id || "",
      instagram: data.social?.instagram_id || "",
      linkedin: data.social?.linkedin_id || "",
      twitter: data.social?.twitter_id || "",
      telegram: data.social?.telegram_id || "",
      youtube: data.social?.youtube_id || ""
    };

    selectedPerks.value = (
      company.value.perksAndBenefits || []
    ).map(item => item.id || item);

    company.value.perksAndBenefits = [
      ...selectedPerks.value
    ];

    /* OLD COMPANY PHOTOS */

    extraImages.value = [];

    if (
      Array.isArray(data.picturesOfCompany) &&
      data.picturesOfCompany.length
    ) {
      extraImages.value =
        data.picturesOfCompany.map(
          item => item?.image_url || ""
        );
    }

    while (extraImages.value.length < 3) {
      extraImages.value.push("");
    }

    extraImages.value =
      extraImages.value.slice(0, 3);

    originalCompany.value =
      JSON.parse(
        JSON.stringify(company.value)
      );

    await nextTick();

  } catch (error) {
    console.error(
      "❌ Ошибка при загрузке компании:",
      error
    );

    showMessage(
      "Не удалось загрузить компанию",
      "error"
    );
  } finally {
    isLoading.value = false;
  }
});


onBeforeUnmount(() => {
  themeObserver?.disconnect();

  vacancyImagePreviews.value.forEach(preview => {
    if (preview.url) {
      URL.revokeObjectURL(preview.url);
    }
  });

  if (snackbarTimer) {
    clearTimeout(snackbarTimer);
  }
});


/* =========================================================
   COMPANY DATA
========================================================= */

async function saveCompany() {
  if (!hasCompanyChanges.value) {
    return;
  }

  companySaving.value = true;

  try {
    const payload = {
        name:
          company.value.name || null,

        specifics:
          company.value.specifics || null,

        email:
          company.value.email || null,

        website:
          company.value.website || null,

        aboutCompany:
          company.value.aboutCompany || null,

        alternativeNames:
          company.value.alternativeNames || []
      };

    await CompanyService.updateAdminCompany(
      company.value.id,
      payload
    );

    originalCompany.value =
      JSON.parse(
        JSON.stringify(company.value)
      );

    showMessage(
      "Компания обновлена"
    );
  } catch (error) {
    console.error(
      "❌ Ошибка сохранения компании:",
      error
    );

    showMessage(
      "Не удалось сохранить компанию",
      "error"
    );
  } finally {
    companySaving.value = false;
  }
}


/* =========================================================
   LOGO
========================================================= */

function onLogoTap() {
  if (logoUploading.value) {
    return;
  }

  logoInput.value?.click();
}


async function onLogoPicked(event) {
  const file =
    event.target.files?.[0];

  if (!file) {
    return;
  }

  logoUploading.value = true;

  try {
    const url =
      await CompanyService.uploadCompanyAdminImage(
        file,
        "logo",
        company.value.id
      );

    company.value.urlOfLogo = url;

    showMessage(
      "Логотип обновлён"
    );
  } catch (error) {
    console.error(
      "❌ Ошибка загрузки логотипа:",
      error
    );

    showMessage(
      "Не удалось загрузить логотип",
      "error"
    );
  } finally {
    logoUploading.value = false;
    event.target.value = "";
  }
}

function addAlternativeName() {
  if (!company.value) {
    return;
  }

  const value =
    newAlternativeName.value
      .trim();

  if (!value) {
    return;
  }

  if (
    !Array.isArray(
      company.value.alternativeNames
    )
  ) {
    company.value.alternativeNames = [];
  }

  const alreadyExists =
    company.value.alternativeNames.some(
      item =>
        item.trim().toLowerCase() ===
        value.toLowerCase()
    );

  if (alreadyExists) {
    showMessage(
      "Такое название уже добавлено",
      "error"
    );

    return;
  }

  company.value.alternativeNames.push(
    value
  );

  newAlternativeName.value = "";
}


function removeAlternativeName(index) {
  if (
    !Array.isArray(
      company.value?.alternativeNames
    )
  ) {
    return;
  }

  company.value.alternativeNames.splice(
    index,
    1
  );
}

/* =========================================================
   BANNER
========================================================= */

function onBannerClick() {
  if (bannerUploading.value) {
    return;
  }

  bannerInputRef.value?.click();
}


async function onBannerPicked(event) {
  const file =
    event.target.files?.[0];

  if (!file) {
    return;
  }

  bannerUploading.value = true;

  try {
    const url =
      await CompanyService.uploadCompanyAdminImage(
        file,
        "background",
        company.value.id
      );

    isBackgroundLoaded.value = false;
    company.value.urlOfBackground = url;

    showMessage(
      "Обложка обновлена"
    );
  } catch (error) {
    console.error(
      "❌ Ошибка загрузки обложки:",
      error
    );

    showMessage(
      "Не удалось загрузить обложку",
      "error"
    );
  } finally {
    bannerUploading.value = false;
    event.target.value = "";
  }
}


/* =========================================================
   REGION + MAP
========================================================= */

function openCompanyRegionDrawer() {
  showCompanyRegionDrawer.value = true;
}


function onCompanyRegionSelected(value) {
  if (!company.value) {
    return;
  }

  if (
    typeof value === "object" &&
    value !== null
  ) {
    company.value.regionId =
      value.id ??
      value.regionId ??
      company.value.regionId;
  } else {
    company.value.regionId = value;
  }

  showCompanyRegionDrawer.value = false;
}


async function saveLocation({
  lat,
  lng,
  address
}) {
  try {
    company.value.companyLocation = {
      latitude: lat,
      longitude: lng,
      address
    };

    await CompanyService.updateAdminCompany(
      company.value.id,
      {
        location: {
          latitude: lat,
          longitude: lng,
          street: address
        }
      }
    );

    showMessage(
      "Локация компании обновлена"
    );
  } catch (error) {
    console.error(
      "❌ Ошибка сохранения локации:",
      error
    );

    showMessage(
      "Не удалось сохранить локацию",
      "error"
    );
  }
}


/* =========================================================
   SOCIAL
========================================================= */

async function saveSocial() {
  if (!hasSocialChanges.value) {
    return;
  }

  socialSaving.value = true;

  try {
    const payload = {
      social_data: {
        facebookId:
          company.value.social.facebook || null,

        facebookEnabled:
          !!company.value.social.facebook,

        instagramId:
          company.value.social.instagram || null,

        instagramEnabled:
          !!company.value.social.instagram,

        linkedinId:
          company.value.social.linkedin || null,

        linkedinEnabled:
          !!company.value.social.linkedin,

        twitterId:
          company.value.social.twitter || null,

        twitterEnabled:
          !!company.value.social.twitter,

        telegramId:
          company.value.social.telegram || null,

        telegramEnabled:
          !!company.value.social.telegram,

        youtubeId:
          company.value.social.youtube || null,

        youtubeEnabled:
          !!company.value.social.youtube
      }
    };

    await CompanyService.updateAdminCompany(
      company.value.id,
      payload
    );

    originalCompany.value.social =
      JSON.parse(
        JSON.stringify(company.value.social)
      );

    showMessage(
      "Социальные сети обновлены"
    );
  } catch (error) {
    console.error(
      "❌ Ошибка сохранения соцсетей:",
      error
    );

    showMessage(
      "Не удалось сохранить соцсети",
      "error"
    );
  } finally {
    socialSaving.value = false;
  }
}


/* =========================================================
   PERKS
========================================================= */

function togglePerk(id) {
  if (
    selectedPerks.value.includes(id)
  ) {
    selectedPerks.value =
      selectedPerks.value.filter(
        item => item !== id
      );
  } else {
    selectedPerks.value.push(id);
  }

  company.value.perksAndBenefits = [
    ...selectedPerks.value
  ];
}


async function savePerks() {
  if (!hasPerksChanges.value) {
    return;
  }

  perksSaving.value = true;

  try {
    const payload = {
      perks_and_benefits:
        ALL_BENEFITS
          .filter(perk => {
            return selectedPerks.value.includes(
              perk.id
            );
          })
          .map(perk => ({
            id: perk.id,
            name: perk.ruName,
            ru_name: perk.ruName,
            eng_name: perk.engName || ""
          }))
    };

    await CompanyService.updateAdminCompany(
      company.value.id,
      payload
    );

    originalCompany.value.perksAndBenefits = [
      ...selectedPerks.value
    ];

    showMessage(
      "Преимущества сохранены"
    );
  } catch (error) {
    console.error(
      "❌ Ошибка сохранения perks:",
      error
    );

    showMessage(
      "Не удалось сохранить преимущества",
      "error"
    );
  } finally {
    perksSaving.value = false;
  }
}


/* =========================================================
   OLD COMPANY EXTRA PHOTOS
========================================================= */

function selectExtraImage(index) {
  extraIndex = index;

  extraInput.value?.click();
}


async function onExtraPicked(event) {
  const file =
    event.target.files?.[0];

  if (!file || extraIndex === null) {
    return;
  }

  const index = extraIndex;

  extraUploading.value[index] = true;

  try {
    extraImages.value[index] =
      await fileToBase64(file);

    const url =
      await CompanyService
        .uploadCompanyExtraImagesWithRenew(
          file,
          "extra",
          true,
          company.value.id
        );

    if (
      !Array.isArray(
        company.value.picturesOfCompany
      )
    ) {
      company.value.picturesOfCompany = [];
    }

    company.value.picturesOfCompany[index] = {
      id:
        company.value.picturesOfCompany?.[index]?.id ||
        null,

      image_url: url
    };

    extraImages.value[index] = url;

    showMessage(
      "Фото компании загружено"
    );
  } catch (error) {
    console.error(
      "❌ Ошибка загрузки фото компании:",
      error
    );

    showMessage(
      "Не удалось загрузить фото компании",
      "error"
    );
  } finally {
    extraUploading.value[index] = false;

    event.target.value = "";

    extraIndex = null;
  }
}


async function removeExtraImage(index) {
  const item =
    company.value
      .picturesOfCompany?.[index];

  if (!item?.id) {
    return;
  }

  extraDeleting.value[index] = true;

  try {
    await CompanyService.updateAdminCompany(
      company.value.id,
      {
        image_delete_id: item.id
      }
    );

    extraImages.value[index] = "";

    company.value
      .picturesOfCompany[index] = null;

    showMessage(
      "Фото удалено"
    );
  } catch (error) {
    console.error(
      "❌ Ошибка удаления фото:",
      error
    );

    showMessage(
      "Не удалось удалить фото",
      "error"
    );
  } finally {
    extraDeleting.value[index] = false;
  }
}


/* =========================================================
   NEW VACANCY IMAGES
========================================================= */

function openVacancyImagesPicker() {
  if (vacancyImagesUploading.value) {
    return;
  }

  vacancyImagesInput.value?.click();
}


function onVacancyImagesPicked(event) {
  const files = Array.from(
    event.target.files || []
  );

  addVacancyImages(files);

  event.target.value = "";
}


async function onVacancyImagesDrop(event) {
  vacancyImagesDragging.value = false;

  if (vacancyImagesUploading.value) {
    return;
  }

  const transfer = event.dataTransfer;

  if (!transfer) {
    return;
  }

  // 1. Обычные файлы с компьютера
  const files = Array.from(transfer.files || []);

  if (files.length) {
    addVacancyImages(files);
    return;
  }

  // 2. Картинка, перетащенная прямо из браузера
  const imageUrl = extractDroppedImageUrl(transfer);

  if (!imageUrl) {
    showMessage("Не удалось определить изображение", "error");
    return;
  }

  try {
    const file = await imageUrlToFile(imageUrl);
    addVacancyImages([file]);
  } catch (error) {
    console.error("❌ Ошибка загрузки изображения по URL:", error);

    showMessage(
      "Не удалось загрузить изображение с этого сайта",
      "error"
    );
  }
}

function extractDroppedImageUrl(dataTransfer) {
  // 1. Сначала пытаемся достать именно <img src="...">
  const html = dataTransfer.getData("text/html");

  if (html) {
    const parsedDocument =
      new DOMParser().parseFromString(
        html,
        "text/html"
      );

    const image =
      parsedDocument.querySelector(
        "img[src]"
      );

    const src =
      image?.getAttribute("src")?.trim();

    if (
      src &&
      /^https?:\/\//i.test(src)
    ) {
      return src;
    }
  }

  // 2. Потом URI
  const uri =
    dataTransfer
      .getData("text/uri-list")
      ?.trim();

  if (uri) {
    const firstUrl =
      uri
        .split("\n")
        .map(item => item.trim())
        .find(item =>
          /^https?:\/\//i.test(item)
        );

    if (firstUrl) {
      return firstUrl;
    }
  }

  // 3. Последний fallback
  const plain =
    dataTransfer
      .getData("text/plain")
      ?.trim();

  if (
    plain &&
    /^https?:\/\//i.test(plain)
  ) {
    return plain;
  }

  return null;
}

async function imageUrlToFile(url) {
  console.log(
    "🖼 Загружаем внешнюю картинку:",
    url
  );

  const blob =
    await AdminService.downloadAdminExternalImage(
      url
    );

  if (
    !blob ||
    !blob.type ||
    !blob.type.startsWith("image/")
  ) {
    throw new Error(
      "Полученный файл не является изображением"
    );
  }

  const extension =
    getImageExtension(blob.type);

  const fileName =
    `browser-image-${Date.now()}.${extension}`;

  return new File(
    [blob],
    fileName,
    {
      type: blob.type,
      lastModified: Date.now()
    }
  );
}

function getImageExtension(mimeType) {
  switch (mimeType) {
    case "image/jpeg":
      return "jpg";
    case "image/png":
      return "png";
    case "image/webp":
      return "webp";
    default:
      return "jpg";
  }
}


function addVacancyImages(files) {
  if (!files.length) {
    return;
  }

  const allowedTypes = new Set([
    "image/jpeg",
    "image/png",
    "image/webp"
  ]);

  const invalidType =
    files.some(file => {
      return !allowedTypes.has(file.type);
    });

  if (invalidType) {
    showMessage(
      "Поддерживаются только JPG, PNG и WEBP",
      "error"
    );
  }

  const tooLarge =
    files.some(file => {
      return (
        file.size >
        MAX_VACANCY_IMAGE_SIZE
      );
    });

  if (tooLarge) {
    showMessage(
      "Размер одной фотографии не должен превышать 15 MB",
      "error"
    );
  }

  const validFiles =
    files.filter(file => {
      return (
        allowedTypes.has(file.type) &&
        file.size <=
          MAX_VACANCY_IMAGE_SIZE
      );
    });

  const existingKeys =
    new Set(
      vacancyImageFiles.value.map(
        file =>
          `${file.name}-${file.size}-${file.lastModified}`
      )
    );

  const uniqueFiles =
    validFiles.filter(file => {
      const key =
        `${file.name}-${file.size}-${file.lastModified}`;

      return !existingKeys.has(key);
    });

  const remaining =
    MAX_VACANCY_IMAGES -
    vacancyImageFiles.value.length;

  if (remaining <= 0) {
    showMessage(
      "Можно выбрать максимум 4 фотографии",
      "error"
    );

    return;
  }

  const filesToAdd =
    uniqueFiles.slice(0, remaining);

  filesToAdd.forEach(file => {
    vacancyImageFiles.value.push(file);

    vacancyImagePreviews.value.push({
      id:
        `${Date.now()}-${file.name}-${Math.random()}`,

      file,

      url: URL.createObjectURL(file)
    });
  });

  if (
    uniqueFiles.length >
    remaining
  ) {
    showMessage(
      "Добавлены первые 4 фотографии",
      "error"
    );
  }
}


function removeVacancyImage(index) {
  if (vacancyImagesUploading.value) {
    return;
  }

  const preview =
    vacancyImagePreviews.value[index];

  if (preview?.url) {
    URL.revokeObjectURL(
      preview.url
    );
  }

  vacancyImagePreviews.value.splice(
    index,
    1
  );

  vacancyImageFiles.value.splice(
    index,
    1
  );
}


function clearVacancyImages() {
  vacancyImagePreviews.value.forEach(
    preview => {
      if (preview.url) {
        URL.revokeObjectURL(
          preview.url
        );
      }
    }
  );

  vacancyImagePreviews.value = [];
  vacancyImageFiles.value = [];
}


async function uploadVacancyImages() {
  if (
    !vacancyImageFiles.value.length ||
    vacancyImagesUploading.value
  ) {
    return;
  }

  const companyId =
    company.value.companyId ||
    company.value.id ||
    route.params.companyId;

  if (!companyId) {
    showMessage(
      "Не удалось определить ID компании",
      "error"
    );

    return;
  }

  vacancyImagesUploading.value = true;

  const expectedCount =
    vacancyImageFiles.value.length;

  try {
    const response =
      await AdminService
        .uploadAdminCompanyImages(
          companyId,
          vacancyImageFiles.value
        );

    console.log(
      "✅ COMPANY VACANCY IMAGES:",
      response
    );

    company.value.isImagesUploaded = true;

    originalCompany.value.isImagesUploaded =
      true;

    const uploadedCount =
      response?.uploaded_count ??
      response?.images?.length ??
      expectedCount;

    clearVacancyImages();

    showMessage(
      `Загружено фотографий: ${uploadedCount}`
    );
  } catch (error) {
    console.error(
      "❌ Ошибка загрузки фотографий для вакансий:",
      error
    );

    showMessage(
      error.response?.data?.error ||
      "Не удалось загрузить фотографии",
      "error"
    );
  } finally {
    vacancyImagesUploading.value = false;
  }
}

</script>

<style scoped>

/* =========================================================
   HR3 COMPANY ADMIN
   FULL LIGHT / DARK THEME
========================================================= */


/* =========================================================
   THEME — DEFAULT / LIGHT
========================================================= */

.company-admin-page.theme-light {

  /* PAGE */

  --admin-page-bg: #f4f6f9;

  --admin-page-glow-blue:
    rgba(55, 120, 238, 0.07);

  --admin-page-glow-purple:
    rgba(111, 83, 232, 0.055);


  /* CARDS */

  --admin-card-bg: #ffffff;

  --admin-card-bg-soft: #f7f8fa;

  --admin-card-bg-hover: #f0f3f7;

  --admin-card-bg-strong: #ffffff;


  /* BORDERS */

  --admin-border:
    rgba(24, 38, 58, 0.07);

  --admin-border-hover:
    rgba(50, 110, 195, 0.16);

  --admin-border-strong:
    #e4e7ec;

  --admin-divider:
    #edf0f3;


  /* TEXT */

  --admin-text: #252930;

  --admin-text-title: #252930;

  --admin-text-secondary: #7f8792;

  --admin-text-muted: #9ba2ad;

  --admin-text-faint: #b2b7bf;

  --admin-text-input: #2c3138;


  /* INPUTS */

  --admin-input-bg: #f7f8fa;

  --admin-input-focus-bg: #ffffff;

  --admin-input-border: #e3e7ec;

  --admin-input-placeholder: #a9afb8;


  /* CHIPS */

  --admin-chip-bg: #f2f4f7;

  --admin-chip-hover-bg: #e9edf3;

  --admin-chip-text: #616a76;


  /* COLORS */

  --admin-blue: #287be7;

  --admin-blue-hover: #216ed2;

  --admin-blue-soft:
    rgba(40, 123, 231, 0.09);

  --admin-blue-soft-hover:
    rgba(40, 123, 231, 0.14);


  --admin-purple: #735ed0;

  --admin-purple-soft:
    rgba(115, 94, 208, 0.09);


  --admin-green: #2c9a69;

  --admin-green-soft:
    rgba(44, 154, 105, 0.09);


  --admin-orange: #d4873d;

  --admin-orange-soft:
    rgba(212, 135, 61, 0.10);


  --admin-red: #d6535e;

  --admin-red-soft:
    rgba(214, 83, 94, 0.09);


  /* OFFICE */

  --admin-office-bg: #f6f7f9;

  --admin-office-icon-bg: #ffffff;


  /* SOCIAL */

  --admin-social-bg: #f7f8fa;

  --admin-social-icon-bg: #eceff3;

  --admin-social-active-bg: #ffffff;


  /* GALLERY */

  --admin-photo-bg: #f7f9fb;

  --admin-photo-border: #d2d8e1;

  --admin-photo-hover-border: #8ab4ec;


  /* PERKS */

  --admin-perk-bg: #f4f5f7;

  --admin-perk-hover-bg: #ebeff4;

  --admin-perk-border: #e5e8ec;


  /* SHADOWS */

  --admin-shadow:
    0 10px 32px
    rgba(29, 48, 74, 0.045);

  --admin-shadow-large:
    0 14px 42px
    rgba(28, 47, 75, 0.065);

  --admin-shadow-hover:
    0 16px 38px
    rgba(31, 55, 90, 0.085);


  /* SKELETON */

  --admin-skeleton: #e7eaee;

  --admin-skeleton-highlight:
    rgba(255, 255, 255, 0.62);


  /* SCROLL */

  --admin-scroll-track: #eef0f3;

  --admin-scroll-thumb: #cbd0d7;

}


/* =========================================================
   THEME — DARK
========================================================= */

.company-admin-page.theme-dark {

  /* PAGE */

  --admin-page-bg: #17181b;

  --admin-page-glow-blue:
    rgba(47, 105, 193, 0.09);

  --admin-page-glow-purple:
    rgba(101, 76, 197, 0.07);


  /* CARDS */

  --admin-card-bg: #202125;

  --admin-card-bg-soft: #292b2f;

  --admin-card-bg-hover: #303238;

  --admin-card-bg-strong: #25262a;


  /* BORDERS */

  --admin-border:
    rgba(255, 255, 255, 0.065);

  --admin-border-hover:
    rgba(91, 145, 218, 0.18);

  --admin-border-strong:
    #383b41;

  --admin-divider:
    #31343a;


  /* TEXT */

  --admin-text: #edf0f4;

  --admin-text-title: #f0f2f5;

  --admin-text-secondary: #9299a4;

  --admin-text-muted: #747c87;

  --admin-text-faint: #5f6670;

  --admin-text-input: #e9ebef;


  /* INPUTS */

  --admin-input-bg: #292b2f;

  --admin-input-focus-bg: #2d2f34;

  --admin-input-border: #383b41;

  --admin-input-placeholder: #676f7b;


  /* CHIPS */

  --admin-chip-bg: #2a2c31;

  --admin-chip-hover-bg: #32353b;

  --admin-chip-text: #aab1ba;


  /* COLORS */

  --admin-blue: #68a4f5;

  --admin-blue-hover: #7eb1f6;

  --admin-blue-soft:
    rgba(69, 133, 224, 0.13);

  --admin-blue-soft-hover:
    rgba(69, 133, 224, 0.18);


  --admin-purple: #9786ea;

  --admin-purple-soft:
    rgba(128, 105, 221, 0.13);


  --admin-green: #69c897;

  --admin-green-soft:
    rgba(58, 174, 113, 0.12);


  --admin-orange: #e1a15f;

  --admin-orange-soft:
    rgba(212, 135, 61, 0.13);


  --admin-red: #e46a74;

  --admin-red-soft:
    rgba(214, 83, 94, 0.13);


  /* OFFICE */

  --admin-office-bg: #292b2f;

  --admin-office-icon-bg: #34363b;


  /* SOCIAL */

  --admin-social-bg: #292b2f;

  --admin-social-icon-bg: #34363b;

  --admin-social-active-bg: #3a3d42;


  /* GALLERY */

  --admin-photo-bg: #292b2f;

  --admin-photo-border: #42464d;

  --admin-photo-hover-border: #668fc7;


  /* PERKS */

  --admin-perk-bg: #292b2f;

  --admin-perk-hover-bg: #31343a;

  --admin-perk-border: #383b41;


  /* SHADOWS */

  --admin-shadow:
    0 12px 34px
    rgba(0, 0, 0, 0.18);

  --admin-shadow-large:
    0 16px 45px
    rgba(0, 0, 0, 0.24);

  --admin-shadow-hover:
    0 18px 42px
    rgba(0, 0, 0, 0.27);


  /* SKELETON */

  --admin-skeleton: #292b30;

  --admin-skeleton-highlight:
    rgba(255, 255, 255, 0.045);


  /* SCROLL */

  --admin-scroll-track: #1d1e21;

  --admin-scroll-thumb: #393c42;

}


/* =========================================================
   PAGE — LOCAL TO THIS COMPONENT
========================================================= */

.company-admin-page {
  min-height: 100vh;

  color:
    var(--admin-text);

  background:
    radial-gradient(
      circle at 8% 4%,
      var(--admin-page-glow-blue),
      transparent 400px
    ),
    radial-gradient(
      circle at 92% 7%,
      var(--admin-page-glow-purple),
      transparent 360px
    ),
    var(--admin-page-bg);

  transition:
    background-color 0.25s ease,
    color 0.25s ease;
}


.page-shell {
  width:
    min(
      1180px,
      calc(100% - 40px)
    );

  margin:
    0 auto;

  padding:
    118px
    0
    100px;
}


/* =========================================================
   PAGE INTRO
========================================================= */

.page-intro {
  display: flex;

  align-items:
    flex-start;

  justify-content:
    space-between;

  gap: 40px;

  margin-bottom:
    30px;
}


.eyebrow {
  margin-bottom:
    7px;

  color:
    var(--admin-text-muted);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    9px;

  letter-spacing:
    0.12em;

  text-transform:
    uppercase;
}


.page-intro h1 {
  margin:
    0;

  color:
    var(--admin-text-title);

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size:
    31px;

  line-height:
    1.15;

  letter-spacing:
    -0.03em;
}


.page-intro p {
  max-width:
    610px;

  margin:
    10px
    0
    0;

  color:
    var(--admin-text-secondary);

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    13px;

  line-height:
    1.6;
}


.admin-status {
  flex-shrink:
    0;

  display:
    flex;

  align-items:
    center;

  gap:
    9px;

  min-height:
    36px;

  padding:
    0
    13px;

  color:
    var(--admin-blue);

  background:
    var(--admin-blue-soft);

  border:
    1px solid
    rgba(67, 132, 224, 0.12);

  border-radius:
    12px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    9px;
}


.admin-status-dot {
  width:
    7px;

  height:
    7px;

  background:
    var(--admin-blue);

  border-radius:
    50%;

  box-shadow:
    0
    0
    0
    4px
    var(--admin-blue-soft);
}


/* =========================================================
   COMPANY HERO
========================================================= */

.company-hero {
  overflow:
    hidden;

  margin-bottom:
    22px;

  background:
    var(--admin-card-bg);

  border:
    1px solid
    var(--admin-border);

  border-radius:
    24px;

  box-shadow:
    var(--admin-shadow-large);

  transition:
    background-color 0.25s ease,
    border-color 0.25s ease,
    box-shadow 0.25s ease;
}


/* =========================================================
   HERO COVER
========================================================= */

.cover-area {
  position:
    relative;

  height:
    280px;

  overflow:
    hidden;

  background:
    var(--admin-card-bg-soft);
}


.cover-image,
.cover-placeholder {
  width:
    100%;

  height:
    100%;
}


.cover-image {
  display:
    block;

  object-fit:
    cover;

  opacity:
    0;

  transition:
    opacity 0.4s ease;
}


.cover-image.visible {
  opacity:
    1;
}


.cover-placeholder {
  position:
    relative;

  overflow:
    hidden;

  background:
    linear-gradient(
      120deg,
      var(--admin-card-bg-soft),
      var(--admin-chip-bg)
    );
}


.cover-placeholder-shape {
  position:
    absolute;

  border-radius:
    50%;

  filter:
    blur(2px);
}


.shape-one {
  width:
    420px;

  height:
    420px;

  top:
    -240px;

  right:
    70px;

  background:
    var(--admin-blue-soft);
}


.shape-two {
  width:
    330px;

  height:
    330px;

  left:
    80px;

  bottom:
    -220px;

  background:
    var(--admin-purple-soft);
}


.cover-overlay {
  position:
    absolute;

  inset:
    0;

  pointer-events:
    none;

  background:
    linear-gradient(
      180deg,
      transparent 45%,
      rgba(7, 14, 25, 0.19)
    );
}


/* =========================================================
   COVER EDIT BUTTON
========================================================= */

.media-edit-button {
  position:
    absolute;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  gap:
    7px;

  min-height:
    38px;

  padding:
    0
    13px;

  color:
    #ffffff;

  background:
    rgba(17, 23, 32, 0.56);

  border:
    1px solid
    rgba(255, 255, 255, 0.14);

  border-radius:
    11px;

  backdrop-filter:
    blur(14px);

  -webkit-backdrop-filter:
    blur(14px);

  cursor:
    pointer;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    9px;

  transition:
    transform 0.18s ease,
    background-color 0.18s ease,
    opacity 0.18s ease;
}


.media-edit-button:hover:not(:disabled) {
  background:
    rgba(17, 23, 32, 0.76);

  transform:
    translateY(-1px);
}


.media-edit-button:disabled {
  opacity:
    0.6;

  cursor:
    wait;
}


.media-edit-button svg {
  width:
    15px;

  height:
    15px;
}


.banner-edit {
  right:
    18px;

  bottom:
    18px;
}


/* =========================================================
   HERO PROFILE
========================================================= */

.hero-profile {
  position: relative;

  min-height: 100px;

  display: block;

  padding:
    98px
    28px
    0px;
}


/* =========================================================
   LOGO
========================================================= */

.logo-shell {
  position:
    absolute;

  left:
    28px;

  top:
    -56px;

  width:
    116px;

  height:
    116px;

  padding:
    5px;

  cursor:
    pointer;

  background:
    var(--admin-card-bg);

  border:
    1px solid
    var(--admin-border);

  border-radius:
    29px;

  box-shadow:
    0 11px 30px
    rgba(18, 36, 62, 0.16);

  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease,
    background-color 0.25s ease;
}


.company-admin-page.theme-dark .logo-shell {
  box-shadow:
    0 13px 32px
    rgba(0, 0, 0, 0.34);
}


.logo-shell:hover {
  transform:
    translateY(-2px);
}


.company-logo,
.logo-placeholder {
  width:
    100%;

  height:
    100%;

  border-radius:
    24px;
}


.company-logo {
  display:
    block;

  object-fit:
    cover;
}


.logo-placeholder {
  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  color:
    var(--admin-blue);

  background:
    linear-gradient(
      145deg,
      var(--admin-blue-soft),
      var(--admin-purple-soft)
    );

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size:
    31px;
}


.logo-edit-overlay {
  position:
    absolute;

  right:
    -4px;

  bottom:
    -4px;

  width:
    33px;

  height:
    33px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  color:
    #ffffff;

  background:
    linear-gradient(
      135deg,
      #287be7,
      #536be2
    );

  border:
    4px solid
    var(--admin-card-bg);

  border-radius:
    50%;

  box-shadow:
    0 5px 13px
    rgba(39, 121, 232, 0.28);
}


.logo-edit-overlay svg {
  width:
    13px;

  height:
    13px;
}


/* =========================================================
   HERO INFO
========================================================= */

.hero-company-info {
  flex:
    1;

  min-width:
    0;
}


.hero-title-row {
  display:
    flex;

  align-items:
    center;

  flex-wrap:
    wrap;

  gap:
    11px;
}


.hero-title-row h2 {
  margin:
    0;

  color:
    var(--admin-text-title);

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size:
    23px;

  letter-spacing:
    -0.025em;
}


.hero-meta {
  display:
    flex;

  align-items:
    center;

  flex-wrap:
    wrap;

  gap:
    8px;

  margin-top:
    11px;
}


/* =========================================================
   HERO PILLS
========================================================= */

.location-pill,
.specialization-pill,
.vacancy-photo-badge {
  display:
    inline-flex;

  align-items:
    center;

  justify-content:
    center;

  gap:
    6px;

  min-height:
    28px;

  padding:
    0
    10px;

  border:
    none;

  border-radius:
    9px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    9px;
}


.location-pill {
  color:
    var(--admin-chip-text);

  background:
    var(--admin-chip-bg);

  cursor:
    pointer;

  transition:
    background-color 0.18s ease,
    transform 0.18s ease;
}


.location-pill:hover {
  background:
    var(--admin-chip-hover-bg);

  transform:
    translateY(-1px);
}


.location-pill svg {
  width:
    13px;

  height:
    13px;
}


.specialization-pill {
  color:
    var(--admin-purple);

  background:
    var(--admin-purple-soft);
}


.vacancy-photo-badge.ready {
  color:
    var(--admin-green);

  background:
    var(--admin-green-soft);
}


.vacancy-photo-badge.missing {
  color:
    var(--admin-red);

  background:
    var(--admin-red-soft);
}


.badge-dot {
  width:
    6px;

  height:
    6px;

  flex-shrink:
    0;

  background:
    currentColor;

  border-radius:
    50%;
}


/* =========================================================
   DASHBOARD GRID
========================================================= */

.dashboard-grid {
  display:
    grid;

  grid-template-columns:
    minmax(0, 1.1fr)
    minmax(360px, 0.9fr);

  gap:
    22px;

  align-items:
    start;
}


/* =========================================================
   DASHBOARD CARDS
========================================================= */

.dashboard-card {
  background:
    var(--admin-card-bg);

  border:
    1px solid
    var(--admin-border);

  border-radius:
    22px;

  box-shadow:
    var(--admin-shadow);

  transition:
    background-color 0.25s ease,
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.18s ease;
}


.dashboard-card:hover {
  border-color:
    var(--admin-border-hover);

  box-shadow:
    var(--admin-shadow-hover);
}


.main-info-card {
  grid-row:
    span 2;

  padding:
    25px;
}


.office-card,
.social-card {
  padding:
    23px;
}


.wide-card {
  margin-top:
    22px;

  padding:
    25px;
}


/* =========================================================
   CARD HEADER
========================================================= */

.card-heading {
  display:
    flex;

  align-items:
    flex-start;

  justify-content:
    space-between;

  gap:
    20px;

  margin-bottom:
    24px;
}


.card-label {
  display:
    block;

  margin-bottom:
    5px;

  color:
    var(--admin-text-muted);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    8px;

  letter-spacing:
    0.08em;

  text-transform:
    uppercase;
}


.card-heading h3 {
  margin:
    0;

  color:
    var(--admin-text-title);

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size:
    17px;

  letter-spacing:
    -0.018em;
}


.card-heading p {
  max-width:
    450px;

  margin:
    6px
    0
    0;

  color:
    var(--admin-text-secondary);

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    10px;

  line-height:
    1.55;
}


/* =========================================================
   SECTION ICONS
========================================================= */

.section-icon {
  width:
    43px;

  height:
    43px;

  flex-shrink:
    0;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  border-radius:
    13px;
}


.section-icon svg {
  width:
    21px;

  height:
    21px;
}


.section-icon.blue {
  color:
    var(--admin-blue);

  background:
    var(--admin-blue-soft);
}


.section-icon.purple {
  color:
    var(--admin-purple);

  background:
    var(--admin-purple-soft);
}


.section-icon.green {
  color:
    var(--admin-green);

  background:
    var(--admin-green-soft);
}


.section-icon.orange {
  color:
    var(--admin-orange);

  background:
    var(--admin-orange-soft);
}


/* =========================================================
   FORM GRID
========================================================= */

.form-grid {
  display:
    grid;

  grid-template-columns:
    repeat(
      2,
      minmax(0, 1fr)
    );

  gap:
    15px;
}


.modern-field {
  min-width:
    0;
}


.modern-field.full {
  grid-column:
    1 / -1;
}


.modern-field > span {
  display:
    block;

  margin:
    0
    0
    7px
    2px;

  color:
    var(--admin-text-secondary);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    9px;
}


.modern-field input,
.modern-field textarea {
  width:
    100%;

  box-sizing:
    border-box;

  color:
    var(--admin-text-input);

  background:
    var(--admin-input-bg);

  border:
    1px solid
    var(--admin-input-border);

  border-radius:
    12px;

  outline:
    none;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    12px;

  transition:
    background-color 0.18s ease,
    border-color 0.18s ease,
    box-shadow 0.18s ease;
}


.modern-field input {
  height:
    45px;

  padding:
    0
    13px;
}


.modern-field textarea {
  min-height:
    145px;

  padding:
    13px;

  resize:
    vertical;

  line-height:
    1.5;
}


.modern-field input::placeholder,
.modern-field textarea::placeholder {
  color:
    var(--admin-input-placeholder);
}


.modern-field input:hover,
.modern-field textarea:hover {
  border-color:
    var(--admin-border-hover);
}


.modern-field input:focus,
.modern-field textarea:focus {
  background:
    var(--admin-input-focus-bg);

  border-color:
    rgba(69, 137, 231, 0.56);

  box-shadow:
    0 0 0 4px
    rgba(49, 121, 226, 0.07);
}


/* =========================================================
   CARD ACTIONS
========================================================= */

.card-actions {
  display:
    flex;

  align-items:
    center;

  justify-content:
    space-between;

  gap:
    18px;

  margin-top:
    22px;

  padding-top:
    18px;

  border-top:
    1px solid
    var(--admin-divider);
}


.unsaved-status,
.saved-status {
  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    9px;
}


.unsaved-status {
  color:
    var(--admin-orange);
}


.saved-status {
  color:
    var(--admin-green);
}


/* =========================================================
   BUTTONS
========================================================= */

.primary-button,
.secondary-button {
  min-height:
    42px;

  display:
    inline-flex;

  align-items:
    center;

  justify-content:
    center;

  gap:
    8px;

  padding:
    0
    16px;

  border-radius:
    12px;

  cursor:
    pointer;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    10px;

  transition:
    transform 0.18s ease,
    opacity 0.18s ease,
    box-shadow 0.18s ease,
    background-color 0.18s ease;
}


.primary-button {
  color:
    #ffffff;

  background:
    linear-gradient(
      135deg,
      #287be7,
      #506ce3
    );

  border:
    none;

  box-shadow:
    0 7px 16px
    rgba(43, 115, 224, 0.18);
}


.secondary-button {
  color:
    var(--admin-blue);

  background:
    var(--admin-blue-soft);

  border:
    1px solid
    rgba(77, 141, 226, 0.16);
}


.primary-button:hover:not(:disabled),
.secondary-button:hover:not(:disabled) {
  transform:
    translateY(-1px);
}


.primary-button:hover:not(:disabled) {
  box-shadow:
    0 10px 22px
    rgba(43, 115, 224, 0.24);
}


.secondary-button:hover:not(:disabled) {
  background:
    var(--admin-blue-soft-hover);
}


.primary-button:disabled,
.secondary-button:disabled {
  opacity:
    0.42;

  cursor:
    default;

  box-shadow:
    none;

  transform:
    none;
}


/* =========================================================
   OFFICE
========================================================= */

.office-address {
  display:
    flex;

  align-items:
    center;

  gap:
    11px;

  padding:
    12px;

  margin-bottom:
    14px;

  background:
    var(--admin-office-bg);

  border:
    1px solid
    var(--admin-border);

  border-radius:
    13px;
}


.office-address-icon {
  width:
    34px;

  height:
    34px;

  flex-shrink:
    0;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  color:
    var(--admin-text-secondary);

  background:
    var(--admin-office-icon-bg);

  border:
    1px solid
    var(--admin-border);

  border-radius:
    10px;
}


.office-address-icon svg {
  width:
    17px;

  height:
    17px;
}


.office-address span,
.office-address strong {
  display:
    block;
}


.office-address span {
  margin-bottom:
    2px;

  color:
    var(--admin-text-muted);

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    8px;
}


.office-address strong {
  color:
    var(--admin-text);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    10px;

  font-weight:
    normal;

  line-height:
    1.4;
}


.map-container {
  overflow:
    hidden;

  border:
    1px solid
    var(--admin-border);

  border-radius:
    15px;
}


/* =========================================================
   SOCIAL NETWORKS
========================================================= */

.social-grid {
  display:
    grid;

  grid-template-columns:
    repeat(
      2,
      minmax(0, 1fr)
    );

  gap:
    10px;
}


.social-input {
  min-width:
    0;

  min-height:
    54px;

  display:
    flex;

  align-items:
    center;

  gap:
    10px;

  padding:
    8px
    10px;

  background:
    var(--admin-social-bg);

  border:
    1px solid
    var(--admin-border-strong);

  border-radius:
    13px;

  transition:
    border-color 0.18s ease,
    background-color 0.18s ease;
}


.social-input:focus-within {
  border-color:
    rgba(69, 137, 231, 0.48);

  background:
    var(--admin-input-focus-bg);
}


.social-icon-box {
  width:
    33px;

  height:
    33px;

  flex-shrink:
    0;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  background:
    var(--admin-social-icon-bg);

  border-radius:
    10px;

  opacity:
    0.55;

  transition:
    background-color 0.18s ease,
    opacity 0.18s ease,
    box-shadow 0.18s ease;
}


.social-icon-box.active {
  opacity:
    1;

  background:
    var(--admin-social-active-bg);

  box-shadow:
    0 3px 8px
    rgba(29, 45, 66, 0.08);
}


.social-icon-box img {
  width:
    17px;

  height:
    17px;

  object-fit:
    contain;
}


.social-input-content {
  min-width:
    0;

  flex:
    1;
}


.social-input-content span {
  display:
    block;

  margin-bottom:
    2px;

  color:
    var(--admin-text-secondary);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    8px;
}


.social-input-content input {
  width:
    100%;

  padding:
    0;

  color:
    var(--admin-text-input);

  background:
    transparent;

  border:
    none;

  outline:
    none;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    9px;
}


.social-input-content input::placeholder {
  color:
    var(--admin-input-placeholder);
}


/* =========================================================
   COMPANY EXTRA PHOTOS
========================================================= */

.gallery-heading {
  margin-bottom:
    18px;
}


.gallery-type-badge {
  flex-shrink:
    0;

  padding:
    7px
    10px;

  color:
    var(--admin-text-secondary);

  background:
    var(--admin-chip-bg);

  border:
    1px solid
    var(--admin-border);

  border-radius:
    9px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    8px;

  white-space:
    nowrap;
}


.company-gallery {
  display:
    grid;

  grid-template-columns:
    repeat(
      3,
      minmax(0, 1fr)
    );

  gap:
    13px;
}


.company-photo-slot {
  position:
    relative;

  min-width:
    0;

  height:
    190px;

  padding:
    0;

  overflow:
    hidden;

  color:
    var(--admin-text);

  background:
    var(--admin-photo-bg);

  border:
    1.5px dashed
    var(--admin-photo-border);

  border-radius:
    17px;

  cursor:
    pointer;

  transition:
    transform 0.18s ease,
    border-color 0.18s ease,
    box-shadow 0.18s ease;
}


.company-photo-slot:hover {
  border-color:
    var(--admin-photo-hover-border);

  transform:
    translateY(-2px);

  box-shadow:
    0 8px 22px
    rgba(34, 64, 104, 0.07);
}


.company-photo-slot.filled {
  border-style:
    solid;

  border-color:
    var(--admin-border);
}


.company-photo-content {
  position:
    relative;

  width:
    100%;

  height:
    100%;
}


.company-photo-content > img {
  width:
    100%;

  height:
    100%;

  display:
    block;

  object-fit:
    cover;
}


.photo-placeholder {
  width:
    100%;

  height:
    100%;

  display:
    flex;

  flex-direction:
    column;

  align-items:
    center;

  justify-content:
    center;

  gap:
    8px;

  color:
    var(--admin-text-secondary);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    9px;
}


.photo-plus {
  width:
    38px;

  height:
    38px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  color:
    var(--admin-blue);

  background:
    var(--admin-blue-soft);

  border-radius:
    12px;

  font-family:
    sans-serif;

  font-size:
    23px;

  font-weight:
    200;
}


.photo-delete-button {
  position:
    absolute;

  top:
    9px;

  right:
    9px;

  width:
    29px;

  height:
    29px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  padding:
    0;

  color:
    #ffffff;

  background:
    rgba(18, 24, 32, 0.64);

  border:
    none;

  border-radius:
    50%;

  backdrop-filter:
    blur(10px);

  -webkit-backdrop-filter:
    blur(10px);

  cursor:
    pointer;

  transition:
    transform 0.18s ease,
    background-color 0.18s ease;
}


.photo-delete-button:hover {
  background:
    var(--admin-red);

  transform:
    scale(1.07);
}


.photo-delete-button svg {
  width:
    13px;

  height:
    13px;
}


.company-photo-content.deleting > img {
  opacity:
    0.42;

  filter:
    blur(2px);

  transition:
    opacity 0.2s ease,
    filter 0.2s ease;
}


.photo-upload-overlay {
  position:
    absolute;

  inset:
    0;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  background:
    rgba(17, 24, 35, 0.36);

  backdrop-filter:
    blur(2px);

  -webkit-backdrop-filter:
    blur(2px);
}


/* =========================================================
   VACANCY IMAGES — ADMIN SECTION
========================================================= */

.vacancy-images-card {
  position:
    relative;

  overflow:
    hidden;

  margin-top:
    22px;

  padding:
    28px;

  color:
    #e9eef6;

  background:
    linear-gradient(
      140deg,
      #171b22 0%,
      #1c212b 55%,
      #1e2634 100%
    );

  border:
    1px solid
    rgba(255, 255, 255, 0.07);

  border-radius:
    24px;

  box-shadow:
    0 18px 45px
    rgba(16, 27, 43, 0.15);
}


.company-admin-page.theme-dark .vacancy-images-card {
  background:
    linear-gradient(
      140deg,
      #202329 0%,
      #242832 55%,
      #252c38 100%
    );

  border-color:
    rgba(255, 255, 255, 0.08);

  box-shadow:
    0 18px 45px
    rgba(0, 0, 0, 0.28);
}


.vacancy-images-glow {
  position:
    absolute;

  top:
    -250px;

  right:
    -100px;

  width:
    380px;

  height:
    380px;

  pointer-events:
    none;

  background:
    rgba(54, 123, 226, 0.18);

  border-radius:
    50%;

  filter:
    blur(10px);
}


/* =========================================================
   VACANCY HEADER
========================================================= */

.vacancy-admin-header {
  position:
    relative;

  display:
    flex;

  align-items:
    flex-start;

  justify-content:
    space-between;

  gap:
    30px;
}


.vacancy-admin-title {
  max-width:
    650px;
}


.vacancy-admin-eyebrow {
  display:
    flex;

  align-items:
    center;

  gap:
    7px;

  margin-bottom:
    8px;

  color:
    #788495;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    8px;

  letter-spacing:
    0.07em;

  text-transform:
    uppercase;
}


.vacancy-admin-eyebrow span {
  padding:
    4px
    7px;

  color:
    #83b7ff;

  background:
    rgba(73, 139, 235, 0.12);

  border:
    1px solid
    rgba(90, 153, 245, 0.15);

  border-radius:
    6px;
}


.vacancy-admin-title h3 {
  margin:
    0;

  color:
    #f4f6f9;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size:
    20px;

  letter-spacing:
    -0.025em;
}


.vacancy-admin-title p {
  max-width:
    610px;

  margin:
    8px
    0
    0;

  color:
    #8994a3;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    10px;

  line-height:
    1.6;
}


/* =========================================================
   VACANCY STATUS
========================================================= */

.vacancy-images-status {
  min-width:
    170px;

  flex-shrink:
    0;

  display:
    flex;

  align-items:
    center;

  gap:
    10px;

  padding:
    11px
    12px;

  background:
    rgba(255, 255, 255, 0.045);

  border:
    1px solid
    rgba(255, 255, 255, 0.065);

  border-radius:
    13px;
}


.status-ring {
  width:
    26px;

  height:
    26px;

  flex-shrink:
    0;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  border-radius:
    50%;
}


.status-dot {
  width:
    7px;

  height:
    7px;

  border-radius:
    50%;
}


.vacancy-images-status.ready
.status-ring {
  background:
    rgba(48, 190, 121, 0.11);
}


.vacancy-images-status.ready
.status-dot {
  background:
    #43ca85;
}


.vacancy-images-status.missing
.status-ring {
  background:
    rgba(227, 88, 99, 0.11);
}


.vacancy-images-status.missing
.status-dot {
  background:
    #e56770;
}


.vacancy-images-status > div > span,
.vacancy-images-status > div > strong {
  display:
    block;
}


.vacancy-images-status > div > span {
  margin-bottom:
    2px;

  color:
    #697688;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    7px;
}


.vacancy-images-status > div > strong {
  color:
    #d9dfe7;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    9px;

  font-weight:
    normal;
}


/* =========================================================
   DROPZONE
========================================================= */

.vacancy-dropzone {
  position:
    relative;

  min-height:
    215px;

  margin-top:
    25px;

  display:
    flex;

  flex-direction:
    column;

  align-items:
    center;

  justify-content:
    center;

  padding:
    26px;

  cursor:
    pointer;

  background:
    linear-gradient(
      145deg,
      rgba(255, 255, 255, 0.025),
      rgba(75, 128, 211, 0.035)
    );

  border:
    1.5px dashed
    rgba(124, 150, 188, 0.28);

  border-radius:
    18px;

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;
}


.vacancy-dropzone:hover {
  background:
    rgba(65, 125, 217, 0.06);

  border-color:
    rgba(91, 151, 239, 0.5);
}


.vacancy-dropzone.dragging {
  background:
    rgba(59, 127, 232, 0.11);

  border-color:
    #5798f2;

  box-shadow:
    inset
    0
    0
    0
    1px
    rgba(87, 152, 242, 0.13);

  transform:
    scale(1.004);
}


.vacancy-dropzone.disabled {
  opacity:
    0.55;

  pointer-events:
    none;
}


/* =========================================================
   DROP ICON
========================================================= */

.drop-icon {
  width:
    58px;

  height:
    58px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  margin-bottom:
    13px;

  color:
    #6ea8fa;

  background:
    linear-gradient(
      145deg,
      rgba(71, 133, 226, 0.16),
      rgba(83, 105, 215, 0.09)
    );

  border:
    1px solid
    rgba(107, 160, 239, 0.12);

  border-radius:
    17px;
}


.drop-icon svg {
  width:
    31px;

  height:
    31px;
}


.drop-title {
  color:
    #e8edf4;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    12px;
}


.drop-subtitle {
  margin-top:
    5px;

  color:
    #7d8998;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    9px;
}


.drop-requirements {
  display:
    flex;

  align-items:
    center;

  gap:
    7px;

  margin-top:
    15px;

  padding:
    6px
    9px;

  color:
    #758294;

  background:
    rgba(255, 255, 255, 0.035);

  border-radius:
    8px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    7px;
}


.requirement-divider {
  width:
    1px;

  height:
    9px;

  background:
    rgba(255, 255, 255, 0.1);
}


/* =========================================================
   VACANCY PREVIEWS
========================================================= */

.vacancy-preview-grid {
  display:
    grid;

  grid-template-columns:
    repeat(
      4,
      minmax(0, 1fr)
    );

  gap:
    11px;

  margin-top:
    17px;
}


.vacancy-preview-card {
  min-width:
    0;

  overflow:
    hidden;

  background:
    rgba(255, 255, 255, 0.045);

  border:
    1px solid
    rgba(255, 255, 255, 0.065);

  border-radius:
    14px;

  transition:
    transform 0.18s ease,
    border-color 0.18s ease;
}


.vacancy-preview-card:hover {
  transform:
    translateY(-1px);

  border-color:
    rgba(107, 160, 239, 0.22);
}


.vacancy-preview-image {
  position:
    relative;

  height:
    130px;

  overflow:
    hidden;
}


.vacancy-preview-image img {
  width:
    100%;

  height:
    100%;

  display:
    block;

  object-fit:
    cover;
}


.preview-index,
.preview-remove {
  position:
    absolute;

  top:
    8px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  color:
    #ffffff;

  background:
    rgba(14, 18, 25, 0.64);

  backdrop-filter:
    blur(11px);

  -webkit-backdrop-filter:
    blur(11px);
}


.preview-index {
  left:
    8px;

  width:
    23px;

  height:
    23px;

  border-radius:
    7px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    8px;
}


.preview-remove {
  right:
    8px;

  width:
    27px;

  height:
    27px;

  padding:
    0;

  border:
    none;

  border-radius:
    50%;

  cursor:
    pointer;

  transition:
    transform 0.18s ease,
    background-color 0.18s ease;
}


.preview-remove:hover:not(:disabled) {
  background:
    #da535e;

  transform:
    scale(1.08);
}


.preview-remove:disabled {
  opacity:
    0.45;

  cursor:
    default;
}


.preview-remove svg {
  width:
    13px;

  height:
    13px;
}


.preview-file-info {
  padding:
    8px
    9px
    9px;
}


.preview-file-name,
.preview-file-size {
  display:
    block;
}


.preview-file-name {
  overflow:
    hidden;

  color:
    #c2c8d0;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    8px;

  text-overflow:
    ellipsis;

  white-space:
    nowrap;
}


.preview-file-size {
  margin-top:
    3px;

  color:
    #687587;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    7px;
}


/* =========================================================
   VACANCY UPLOAD FOOTER
========================================================= */

.vacancy-upload-footer {
  display:
    flex;

  align-items:
    center;

  justify-content:
    space-between;

  gap:
    24px;

  margin-top:
    19px;
}


.vacancy-selection-info {
  display:
    flex;

  align-items:
    center;

  gap:
    11px;
}


.selection-count {
  min-width:
    45px;

  height:
    42px;

  display:
    flex;

  align-items:
    baseline;

  justify-content:
    center;

  padding-top:
    8px;

  box-sizing:
    border-box;

  background:
    rgba(255, 255, 255, 0.045);

  border:
    1px solid
    rgba(255, 255, 255, 0.05);

  border-radius:
    11px;
}


.selection-count strong {
  color:
    #7eb2fb;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size:
    15px;
}


.selection-count span {
  color:
    #637082;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    8px;
}


.vacancy-selection-info
> div:last-child
> strong,
.vacancy-selection-info
> div:last-child
> span {
  display:
    block;
}


.vacancy-selection-info
> div:last-child
> strong {
  margin-bottom:
    3px;

  color:
    #bfc7d1;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    9px;

  font-weight:
    normal;
}


.vacancy-selection-info
> div:last-child
> span {
  color:
    #6d7989;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    8px;
}


/* =========================================================
   VACANCY UPLOAD BUTTON
========================================================= */

.vacancy-upload-button {
  min-width:
    190px;

  height:
    43px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  gap:
    8px;

  padding:
    0
    17px;

  color:
    #ffffff;

  background:
    linear-gradient(
      135deg,
      #397fe7,
      #5169df
    );

  border:
    none;

  border-radius:
    12px;

  cursor:
    pointer;

  box-shadow:
    0 8px 19px
    rgba(42, 102, 210, 0.22);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    9px;

  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease,
    background-color 0.18s ease;
}


.vacancy-upload-button:hover:not(:disabled) {
  transform:
    translateY(-1px);

  box-shadow:
    0 10px 25px
    rgba(42, 102, 210, 0.31);
}


.vacancy-upload-button:disabled {
  color:
    #637082;

  background:
    rgba(255, 255, 255, 0.08);

  cursor:
    default;

  box-shadow:
    none;
}


.vacancy-upload-button svg {
  width:
    16px;

  height:
    16px;
}


/* =========================================================
   PERKS
========================================================= */

.perks-grid {
  display:
    flex;

  flex-wrap:
    wrap;

  gap:
    8px;
}


.perk-chip {
  min-height:
    34px;

  display:
    inline-flex;

  align-items:
    center;

  gap:
    6px;

  padding:
    0
    11px;

  color:
    var(--admin-text-secondary);

  background:
    var(--admin-perk-bg);

  border:
    1px solid
    var(--admin-perk-border);

  border-radius:
    10px;

  cursor:
    pointer;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    9px;

  transition:
    color 0.17s ease,
    background-color 0.17s ease,
    border-color 0.17s ease,
    transform 0.17s ease;
}


.perk-chip:hover {
  background:
    var(--admin-perk-hover-bg);

  transform:
    translateY(-1px);
}


.perk-chip.selected {
  color:
    var(--admin-blue);

  background:
    var(--admin-blue-soft);

  border-color:
    rgba(70, 137, 225, 0.22);
}


.perk-check {
  width:
    16px;

  height:
    16px;

  display:
    none;

  align-items:
    center;

  justify-content:
    center;

  color:
    #ffffff;

  background:
    var(--admin-blue);

  border-radius:
    5px;

  font-size:
    8px;
}


.perk-check.visible {
  display:
    flex;
}


/* =========================================================
   SKELETON
========================================================= */

.loading-shell {
  padding-top:
    130px;
}


.skeleton {
  position:
    relative;

  overflow:
    hidden;

  background:
    var(--admin-skeleton);

  border-radius:
    22px;
}


.skeleton::after {
  content:
    "";

  position:
    absolute;

  inset:
    0;

  transform:
    translateX(-100%);

  background:
    linear-gradient(
      90deg,
      transparent,
      var(--admin-skeleton-highlight),
      transparent
    );

  animation:
    skeleton-shimmer
    1.4s
    infinite;
}


.hero-skeleton {
  height:
    390px;
}


.skeleton-grid {
  display:
    grid;

  grid-template-columns:
    1fr
    0.8fr;

  gap:
    22px;

  margin-top:
    22px;
}


.card-skeleton {
  height:
    370px;
}


.wide-skeleton {
  height:
    240px;

  margin-top:
    22px;
}


@keyframes skeleton-shimmer {
  100% {
    transform:
      translateX(100%);
  }
}


/* =========================================================
   SPINNERS
========================================================= */

.small-spinner,
.button-spinner,
.tiny-spinner {
  display:
    inline-block;

  border-style:
    solid;

  border-color:
    rgba(255, 255, 255, 0.28);

  border-top-color:
    #ffffff;

  border-radius:
    50%;

  animation:
    admin-spinner
    0.7s
    linear
    infinite;
}


.small-spinner {
  width:
    13px;

  height:
    13px;

  border-width:
    2px;
}


.button-spinner {
  width:
    15px;

  height:
    15px;

  border-width:
    2px;
}


.button-spinner.dark {
  border-color:
    rgba(55, 116, 200, 0.22);

  border-top-color:
    var(--admin-blue);
}


.tiny-spinner {
  width:
    12px;

  height:
    12px;

  border-width:
    2px;
}


@keyframes admin-spinner {
  to {
    transform:
      rotate(360deg);
  }
}


/* =========================================================
   PREVIEW TRANSITION
========================================================= */

.vacancy-preview-enter-active,
.vacancy-preview-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}


.vacancy-preview-enter-from,
.vacancy-preview-leave-to {
  opacity:
    0;

  transform:
    scale(0.94);
}


/* =========================================================
   FIXED HEADER
========================================================= */

.fixed-header {
  position:
    fixed;

  top:
    0;

  left:
    0;

  right:
    0;

  z-index:
    1000;
}


/* =========================================================
   LOCAL SCROLLBAR
   Scoped to the component root only.
========================================================= */

.company-admin-page {
  scrollbar-color:
    var(--admin-scroll-thumb)
    var(--admin-scroll-track);
}


.company-admin-page::-webkit-scrollbar {
  width:
    10px;
}


.company-admin-page::-webkit-scrollbar-track {
  background:
    var(--admin-scroll-track);
}


.company-admin-page::-webkit-scrollbar-thumb {
  background:
    var(--admin-scroll-thumb);

  border:
    3px solid
    var(--admin-scroll-track);

  border-radius:
    999px;
}


/* =========================================================
   TABLET
========================================================= */

@media (
  max-width: 980px
) {

  .page-shell {
    width:
      min(
        calc(100% - 30px),
        860px
      );
  }


  .dashboard-grid {
    grid-template-columns:
      1fr;
  }


  .main-info-card {
    grid-row:
      auto;
  }


  .vacancy-preview-grid {
    grid-template-columns:
      repeat(
        2,
        minmax(0, 1fr)
      );
  }


  .vacancy-admin-header {
    gap:
      18px;
  }

}


/* =========================================================
   MOBILE
========================================================= */

@media (
  max-width: 640px
) {

  .page-shell {
    width:
      calc(100% - 22px);

    padding-top:
      96px;

    padding-bottom:
      80px;
  }


  /* INTRO */

  .page-intro {
    flex-direction:
      column;

    gap:
      16px;

    margin-bottom:
      22px;
  }


  .page-intro h1 {
    font-size:
      25px;
  }


  .page-intro p {
    font-size:
      11px;
  }


  /* HERO */

  .company-hero {
    border-radius:
      19px;
  }


  .cover-area {
    height:
      190px;
  }


  .banner-edit {
    right:
      12px;

    bottom:
      12px;
  }


  .media-edit-button {
    min-height:
      34px;

    padding:
      0
      10px;

    font-size:
      8px;
  }


  .hero-profile {
    display:
      block;

    min-height:
      160px;

    padding:
      72px
      18px
      20px;
  }


  .logo-shell {
    left:
      18px;

    top:
      -48px;

    width:
      96px;

    height:
      96px;

    border-radius:
      24px;
  }


  .company-logo,
  .logo-placeholder {
    border-radius:
      19px;
  }


  .logo-placeholder {
    font-size:
      26px;
  }


  .hero-title-row {
    display:
      block;
  }


  .hero-title-row h2 {
    font-size:
      20px;
  }


  .vacancy-photo-badge {
    margin-top:
      8px;
  }


  /* CARDS */

  .dashboard-card,
  .wide-card {
    padding:
      19px;

    border-radius:
      19px;
  }


  .wide-card {
    margin-top:
      16px;
  }


  /* CARD HEADER */

  .card-heading {
    gap:
      14px;

    margin-bottom:
      20px;
  }


  .card-heading h3 {
    font-size:
      15px;
  }


  .section-icon {
    width:
      38px;

    height:
      38px;
  }


  .section-icon svg {
    width:
      18px;

    height:
      18px;
  }


  /* FORMS */

  .form-grid,
  .social-grid {
    grid-template-columns:
      1fr;
  }


  /* CARD ACTIONS */

  .card-actions {
    align-items:
      stretch;

    flex-direction:
      column;
  }


  .primary-button,
  .secondary-button {
    width:
      100%;
  }


  /* GALLERY */

  .gallery-heading {
    flex-direction:
      column;
  }


  .gallery-type-badge {
    align-self:
      flex-start;
  }


  .company-gallery {
    grid-template-columns:
      1fr;
  }


  .company-photo-slot {
    height:
      210px;
  }


  /* VACANCY ADMIN */

  .vacancy-images-card {
    margin-top:
      16px;

    padding:
      19px;

    border-radius:
      19px;
  }


  .vacancy-admin-header {
    flex-direction:
      column;
  }


  .vacancy-admin-title h3 {
    font-size:
      17px;
  }


  .vacancy-images-status {
    width:
      100%;

    box-sizing:
      border-box;
  }


  .vacancy-dropzone {
    min-height:
      190px;

    padding:
      20px;
  }


  .vacancy-preview-grid {
    grid-template-columns:
      repeat(
        2,
        minmax(0, 1fr)
      );
  }


  .vacancy-preview-image {
    height:
      105px;
  }


  .vacancy-upload-footer {
    align-items:
      stretch;

    flex-direction:
      column;
  }


  .vacancy-upload-button {
    width:
      100%;
  }


  .vacancy-selection-info {
    width:
      100%;
  }

}


/* =========================================================
   VERY SMALL
========================================================= */

@media (
  max-width: 390px
) {

  .hero-meta {
    align-items:
      flex-start;

    flex-direction:
      column;
  }


  .location-pill,
  .specialization-pill {
    max-width:
      100%;
  }


  .vacancy-preview-grid {
    grid-template-columns:
      1fr;
  }


  .vacancy-preview-image {
    height:
      165px;
  }


  .drop-requirements {
    flex-wrap:
      wrap;

    justify-content:
      center;
  }

}


/* =========================================================
   REDUCED MOTION
========================================================= */

@media (
  prefers-reduced-motion: reduce
) {

  *,
  *::before,
  *::after {
    scroll-behavior:
      auto !important;

    animation-duration:
      0.01ms !important;

    animation-iteration-count:
      1 !important;

    transition-duration:
      0.01ms !important;
  }

}

/* =========================================================
   VACANCY IMAGES
   LIGHT THEME
========================================================= */

.vacancy-images-card {
  position: relative;
  overflow: hidden;

  margin-top: 22px;
  padding: 28px;

  color: #252930;

  background:
    linear-gradient(
      145deg,
      #ffffff 0%,
      #fbfcfe 100%
    );

  border: 1px solid #e5e9ef;
  border-radius: 24px;

  box-shadow:
    0 14px 40px
    rgba(31, 48, 73, 0.07);

  transition:
    background-color 0.25s ease,
    border-color 0.25s ease,
    box-shadow 0.25s ease;
}


.vacancy-images-glow {
  position: absolute;

  top: -240px;
  right: -100px;

  width: 390px;
  height: 390px;

  pointer-events: none;

  background:
    rgba(55, 124, 235, 0.08);

  border-radius: 50%;

  filter: blur(12px);
}


/* =========================================================
   HEADER
========================================================= */

.vacancy-admin-header {
  position: relative;

  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  gap: 30px;
}


.vacancy-admin-title {
  max-width: 650px;
}


.vacancy-admin-eyebrow {
  display: flex;
  align-items: center;
  gap: 7px;

  margin-bottom: 8px;

  color: #8e96a2;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;
  letter-spacing: 0.07em;

  text-transform: uppercase;
}


.vacancy-admin-eyebrow span {
  padding: 4px 7px;

  color: #347bd9;

  background:
    rgba(52, 123, 217, 0.08);

  border:
    1px solid
    rgba(52, 123, 217, 0.12);

  border-radius: 6px;
}


.vacancy-admin-title h3 {
  margin: 0;

  color: #262b32;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 20px;

  letter-spacing: -0.025em;
}


.vacancy-admin-title p {
  max-width: 610px;

  margin: 8px 0 0;

  color: #818a96;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10px;
  line-height: 1.6;
}


/* =========================================================
   STATUS
========================================================= */

.vacancy-images-status {
  min-width: 170px;

  flex-shrink: 0;

  display: flex;
  align-items: center;

  gap: 10px;

  padding: 11px 12px;

  background: #f6f8fb;

  border:
    1px solid
    #e8ebf0;

  border-radius: 13px;
}


.status-ring {
  width: 26px;
  height: 26px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;
}


.status-dot {
  width: 7px;
  height: 7px;

  border-radius: 50%;
}


.vacancy-images-status.ready .status-ring {
  background:
    rgba(45, 177, 112, 0.10);
}


.vacancy-images-status.ready .status-dot {
  background: #36b777;
}


.vacancy-images-status.missing .status-ring {
  background:
    rgba(222, 82, 92, 0.09);
}


.vacancy-images-status.missing .status-dot {
  background: #df5c66;
}


.vacancy-images-status > div > span,
.vacancy-images-status > div > strong {
  display: block;
}


.vacancy-images-status > div > span {
  margin-bottom: 2px;

  color: #a0a6af;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 7px;
}


.vacancy-images-status > div > strong {
  color: #505760;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;
  font-weight: normal;
}


/* =========================================================
   DROPZONE
========================================================= */

.vacancy-dropzone {
  position: relative;

  min-height: 215px;

  margin-top: 25px;

  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;

  padding: 26px;

  cursor: pointer;

  background:
    linear-gradient(
      145deg,
      #fafcff,
      #f7f9fc
    );

  border:
    1.5px dashed
    #cad5e4;

  border-radius: 18px;

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;
}


.vacancy-dropzone:hover {
  background: #f5f9ff;

  border-color:
    #7da9e4;
}


.vacancy-dropzone.dragging {
  background:
    rgba(54, 126, 230, 0.07);

  border-color:
    #4d91ee;

  box-shadow:
    inset
    0
    0
    0
    1px
    rgba(77, 145, 238, 0.1);

  transform:
    scale(1.004);
}


.vacancy-dropzone.disabled {
  opacity: 0.55;

  pointer-events: none;
}


/* =========================================================
   DROP ICON
========================================================= */

.drop-icon {
  width: 58px;
  height: 58px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-bottom: 13px;

  color: #377fdf;

  background:
    linear-gradient(
      145deg,
      rgba(55, 127, 223, 0.11),
      rgba(91, 104, 220, 0.07)
    );

  border:
    1px solid
    rgba(55, 127, 223, 0.09);

  border-radius: 17px;
}


.drop-icon svg {
  width: 31px;
  height: 31px;
}


.drop-title {
  color: #353b44;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 12px;
}


.drop-subtitle {
  margin-top: 5px;

  color: #9098a4;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9px;
}


/* =========================================================
   REQUIREMENTS
========================================================= */

.drop-requirements {
  display: flex;
  align-items: center;

  gap: 7px;

  margin-top: 15px;

  padding: 6px 9px;

  color: #87909b;

  background: #eef2f6;

  border-radius: 8px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 7px;
}


.requirement-divider {
  width: 1px;
  height: 9px;

  background: #d4d9e0;
}


/* =========================================================
   PREVIEW GRID
========================================================= */

.vacancy-preview-grid {
  display: grid;

  grid-template-columns:
    repeat(
      4,
      minmax(0, 1fr)
    );

  gap: 11px;

  margin-top: 17px;
}


.vacancy-preview-card {
  min-width: 0;

  overflow: hidden;

  background: #f7f8fa;

  border:
    1px solid
    #e5e8ed;

  border-radius: 14px;

  transition:
    transform 0.18s ease,
    border-color 0.18s ease,
    box-shadow 0.18s ease;
}


.vacancy-preview-card:hover {
  transform: translateY(-1px);

  border-color:
    #b9cee9;

  box-shadow:
    0 7px 18px
    rgba(31, 56, 88, 0.07);
}


.vacancy-preview-image {
  position: relative;

  height: 130px;

  overflow: hidden;
}


.vacancy-preview-image img {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;
}


/* =========================================================
   PREVIEW CONTROLS
========================================================= */

.preview-index,
.preview-remove {
  position: absolute;

  top: 8px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #ffffff;

  background:
    rgba(19, 26, 36, 0.67);

  backdrop-filter:
    blur(11px);

  -webkit-backdrop-filter:
    blur(11px);
}


.preview-index {
  left: 8px;

  width: 23px;
  height: 23px;

  border-radius: 7px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;
}


.preview-remove {
  right: 8px;

  width: 27px;
  height: 27px;

  padding: 0;

  border: none;
  border-radius: 50%;

  cursor: pointer;

  transition:
    transform 0.18s ease,
    background-color 0.18s ease;
}


.preview-remove:hover:not(:disabled) {
  background: #d95560;

  transform: scale(1.08);
}


.preview-remove svg {
  width: 13px;
  height: 13px;
}


/* =========================================================
   FILE INFO
========================================================= */

.preview-file-info {
  padding:
    8px
    9px
    9px;
}


.preview-file-name,
.preview-file-size {
  display: block;
}


.preview-file-name {
  overflow: hidden;

  color: #59616c;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  text-overflow: ellipsis;

  white-space: nowrap;
}


.preview-file-size {
  margin-top: 3px;

  color: #9ba2ac;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 7px;
}


/* =========================================================
   FOOTER
========================================================= */

.vacancy-upload-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 24px;

  margin-top: 19px;
}


.vacancy-selection-info {
  display: flex;
  align-items: center;

  gap: 11px;
}


.selection-count {
  min-width: 45px;
  height: 42px;

  display: flex;
  align-items: baseline;
  justify-content: center;

  box-sizing: border-box;

  padding-top: 8px;

  background: #f2f5f8;

  border:
    1px solid
    #e5e9ee;

  border-radius: 11px;
}


.selection-count strong {
  color: #367ddd;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 15px;
}


.selection-count span {
  color: #a1a8b1;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 8px;
}


.vacancy-selection-info
> div:last-child
> strong,
.vacancy-selection-info
> div:last-child
> span {
  display: block;
}


.vacancy-selection-info
> div:last-child
> strong {
  margin-bottom: 3px;

  color: #505761;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;
  font-weight: normal;
}


.vacancy-selection-info
> div:last-child
> span {
  color: #969da7;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 8px;
}


/* =========================================================
   UPLOAD BUTTON
========================================================= */

.vacancy-upload-button {
  min-width: 190px;
  height: 43px;

  display: flex;
  align-items: center;
  justify-content: center;

  gap: 8px;

  padding:
    0
    17px;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #397fe7,
      #5169df
    );

  border: none;
  border-radius: 12px;

  cursor: pointer;

  box-shadow:
    0 8px 19px
    rgba(42, 102, 210, 0.20);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease,
    opacity 0.18s ease;
}


.vacancy-upload-button:hover:not(:disabled) {
  transform:
    translateY(-1px);

  box-shadow:
    0 11px 24px
    rgba(42, 102, 210, 0.27);
}


.vacancy-upload-button:disabled {
  color: #9da5af;

  background: #e9edf2;

  cursor: default;

  box-shadow: none;
}


.vacancy-upload-button svg {
  width: 16px;
  height: 16px;
}


/* =========================================================
   DARK THEME — LOCAL ONLY
========================================================= */

.company-admin-page.theme-dark
.vacancy-images-card {
  color: #edf0f4;

  background:
    linear-gradient(
      145deg,
      #202226 0%,
      #23262c 100%
    );

  border-color:
    #34373d;

  box-shadow:
    0 16px 42px
    rgba(0, 0, 0, 0.24);
}


.company-admin-page.theme-dark
.vacancy-images-glow {
  background:
    rgba(55, 124, 235, 0.12);
}


.company-admin-page.theme-dark
.vacancy-admin-eyebrow {
  color: #788392;
}


.company-admin-page.theme-dark
.vacancy-admin-eyebrow span {
  color: #83b7ff;

  background:
    rgba(73, 139, 235, 0.12);

  border-color:
    rgba(90, 153, 245, 0.15);
}


.company-admin-page.theme-dark
.vacancy-admin-title h3 {
  color: #f1f3f6;
}


.company-admin-page.theme-dark
.vacancy-admin-title p {
  color: #8993a0;
}


.company-admin-page.theme-dark
.vacancy-images-status {
  background:
    #2b2e34;

  border-color:
    #3a3e45;
}


.company-admin-page.theme-dark
.vacancy-images-status
> div > span {
  color: #747f8e;
}


.company-admin-page.theme-dark
.vacancy-images-status
> div > strong {
  color: #d9dee5;
}


/* =========================================================
   DARK DROPZONE
========================================================= */

.company-admin-page.theme-dark
.vacancy-dropzone {
  background:
    linear-gradient(
      145deg,
      #24272d,
      #252a33
    );

  border-color:
    #414b5c;
}


.company-admin-page.theme-dark
.vacancy-dropzone:hover {
  background:
    #272d37;

  border-color:
    #527fb9;
}


.company-admin-page.theme-dark
.vacancy-dropzone.dragging {
  background:
    rgba(59, 127, 232, 0.11);

  border-color:
    #5798f2;
}


.company-admin-page.theme-dark
.drop-icon {
  color: #72aafb;

  background:
    linear-gradient(
      145deg,
      rgba(71, 133, 226, 0.16),
      rgba(83, 105, 215, 0.09)
    );

  border-color:
    rgba(107, 160, 239, 0.12);
}


.company-admin-page.theme-dark
.drop-title {
  color: #e7ebf0;
}


.company-admin-page.theme-dark
.drop-subtitle {
  color: #7f8997;
}


.company-admin-page.theme-dark
.drop-requirements {
  color: #788596;

  background:
    rgba(255, 255, 255, 0.045);
}


.company-admin-page.theme-dark
.requirement-divider {
  background:
    rgba(255, 255, 255, 0.10);
}


/* =========================================================
   DARK PREVIEWS
========================================================= */

.company-admin-page.theme-dark
.vacancy-preview-card {
  background:
    #292c32;

  border-color:
    #393d44;
}


.company-admin-page.theme-dark
.vacancy-preview-card:hover {
  border-color:
    #4d6585;

  box-shadow:
    0 8px 22px
    rgba(0, 0, 0, 0.18);
}


.company-admin-page.theme-dark
.preview-file-name {
  color: #c5cad2;
}


.company-admin-page.theme-dark
.preview-file-size {
  color: #737e8e;
}


/* =========================================================
   DARK FOOTER
========================================================= */

.company-admin-page.theme-dark
.selection-count {
  background:
    #292d34;

  border-color:
    #393e46;
}


.company-admin-page.theme-dark
.selection-count strong {
  color: #7eb2fb;
}


.company-admin-page.theme-dark
.selection-count span {
  color: #667384;
}


.company-admin-page.theme-dark
.vacancy-selection-info
> div:last-child
> strong {
  color: #c3c9d1;
}


.company-admin-page.theme-dark
.vacancy-selection-info
> div:last-child
> span {
  color: #717c8b;
}


.company-admin-page.theme-dark
.vacancy-upload-button:disabled {
  color: #697686;

  background:
    #30343c;
}


/* =========================================================
   MOBILE
========================================================= */

@media (max-width: 640px) {

  .vacancy-images-card {
    padding: 19px;

    border-radius: 19px;
  }


  .vacancy-admin-header {
    flex-direction: column;
  }


  .vacancy-images-status {
    width: 100%;

    box-sizing: border-box;
  }


  .vacancy-dropzone {
    min-height: 190px;

    padding: 20px;
  }


  .vacancy-preview-grid {
    grid-template-columns:
      repeat(
        2,
        minmax(0, 1fr)
      );
  }


  .vacancy-preview-image {
    height: 105px;
  }


  .vacancy-upload-footer {
    flex-direction: column;

    align-items: stretch;
  }


  .vacancy-upload-button {
    width: 100%;
  }

}

/* =========================================================
   ALTERNATIVE NAMES
========================================================= */

.alternative-names-field {
  margin-top: 2px;
}

.alternative-names-list {
  display: flex;

  flex-wrap: wrap;

  gap: 7px;

  margin-bottom: 10px;
}

.alternative-name-chip {
  min-height: 30px;

  display: inline-flex;

  align-items: center;

  gap: 7px;

  padding:
    0
    7px
    0
    11px;

  color:
    var(--admin-chip-text);

  background:
    var(--admin-chip-bg);

  border:
    1px solid
    var(--admin-border);

  border-radius: 10px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  transition:
    background-color 0.18s ease,
    border-color 0.18s ease;
}

.alternative-name-chip:hover {
  background:
    var(--admin-chip-hover-bg);

  border-color:
    var(--admin-border-hover);
}

.alternative-name-remove {
  width: 21px;

  height: 21px;

  display: flex;

  align-items: center;

  justify-content: center;

  padding: 0;

  color:
    var(--admin-text-muted);

  background:
    transparent;

  border:
    none;

  border-radius: 6px;

  cursor: pointer;

  font-size: 16px;

  line-height: 1;

  transition:
    color 0.18s ease,
    background-color 0.18s ease;
}

.alternative-name-remove:hover {
  color:
    var(--admin-red);

  background:
    var(--admin-red-soft);
}

.alternative-name-input-row {
  display: grid;

  grid-template-columns:
    minmax(0, 1fr)
    auto;

  gap: 9px;

  align-items: center;
}

.alternative-name-add-button {
  min-height: 45px;

  display: inline-flex;

  align-items: center;

  justify-content: center;

  gap: 6px;

  padding:
    0
    14px;

  color:
    var(--admin-blue);

  background:
    var(--admin-blue-soft);

  border:
    1px solid
    rgba(77, 141, 226, 0.16);

  border-radius: 12px;

  cursor: pointer;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  white-space: nowrap;

  transition:
    transform 0.18s ease,
    background-color 0.18s ease,
    opacity 0.18s ease;
}

.alternative-name-add-button:hover:not(:disabled) {
  background:
    var(--admin-blue-soft-hover);

  transform:
    translateY(-1px);
}

.alternative-name-add-button:disabled {
  opacity: 0.4;

  cursor: default;
}

.alternative-name-add-button svg {
  width: 14px;

  height: 14px;
}

.field-hint {
  display: block;

  margin:
    7px
    0
    0
    2px;

  color:
    var(--admin-text-muted);

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 8px;

  line-height: 1.45;
}

</style>